import React, { useState, useCallback, useEffect, useRef } from 'react';
import axios from 'axios';
import { getAuthToken, getStudentData, saveApplicationFormDraft, clearApplicationFormDraft } from 'utils/storage';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Grid,
  TextField,
  MenuItem,
  Typography,
  Paper,
  Divider,
  Box,
  Avatar,
  IconButton,
  Alert,
  CircularProgress,
  Collapse,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { format, parseISO, isValid } from 'date-fns';
import { useTranslation } from 'react-i18next';
import CasteOptions from '../enum/CasteOptions';
import { TENTH_CLASS_SUBJECTS, ELEVENTH_TWELFTH_CLASS_SUBJECTS_GROUPED, ELEVENTH_TWELFTH_CLASS_SUBJECTS } from '../enum/SubjectOptions';
import { useSubjects, useVocationalSubjects } from '../../hooks/useSubjects';
import PhotoCameraIcon from '@mui/icons-material/PhotoCamera';
import DeleteIcon from '@mui/icons-material/Delete';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { useApplicationPhotoInfo } from '../../hooks/useApplicationForm';
import { useAuth } from '../../contexts/AuthContext';
import { useMarkSheets } from '../../hooks/useMarkSheets';

const steps = [
  'application_enrollment_details',
  'personal_information',
  'contact_address',
  'bank_financial',
  'admission_details',
  'subjects_academic'
];

// Subject options are now dynamically generated based on class data

export interface AdmissionFromData {
  id?: number;
  // Step 1: Application & Enrollment Details
  applicationNo: string;
  rollNo: string;
  enrolmentNo: string;
  scholarNo: string;
  sssmid: string;
  penNo: string;
  apparId: string;
  studentCode?: string; // Student code field

  // Step 2: Student Personal Information
  studentName: string;
  fatherName: string;
  motherName: string;
  dateOfBirth: string;
  gender: string;
  religion: string;
  caste: string;
  subCaste: string;
  casteCertificate: string;
  aadharNo: string;
  studentPhoto?: File | null; // For new uploads
  studentPhotoUrl?: string; // For existing photos (from backend)
  studentSignature?: File | null; // For new signature uploads
  studentSignatureUrl?: string; // For existing signatures (from backend)

  // Step 3: Contact & Address Information
  houseNo: string;
  street: string;
  landmark: string;
  area: string;
  city: string;
  postOffice: string;
  district: string;
  state: string;
  pinCode: string;
  mobileNo: string;
  gmail: string;

  // Step 4: Bank & Financial Information
  bankName: string;
  ifcCode: string;
  accountNo: string;
  annualIncome: string;

  // Step 5: Admission Details
  admissionDate: string;
  admissionClass: string;
  section: string;
  medium: string;
  admissionType: string; // Admission Type field
  stream: string; // Stream field
  cwsnCode: string; // CWSN Code field
  sambalKramank: string; // Sambal Kramank field

  // Step 6: Subjects - Backend expects List<String> (array of subject codes)
  subjects: string[];

  // System fields
  schoolId: number;
  classId?: number; // Class ID field for backend
  isRegistered: string; // "pending" | "approved" | "rejected"
  sessionId: number;
  sessionName?: string;
}

export const AdmissionFromModal: React.FC<{
  open: boolean;
  onClose: () => void;
  onSubmit: (formData: AdmissionFromData) => void;
  initialData?: AdmissionFromData;
  isEdit?: boolean;
  schoolData?: {
    nameofSchool?: string;
    blockName?: string;
    district?: string;
    diseCode?: string;
    state?: string;
    pinNo?: string;
    schoolCode?: string;
    schoolLogo?: string;
    medium?: string | string[];
  };

  selectedClass?: {
    id?: number;
    name: string;
    section: string;
    schoolId: number;
    sessionId?: number;
  } | null;
  availableClasses?: Array<{
    id?: number;
    name: string;
    section: string;
    schoolId: number;
    sessionId?: number;
  }>;
  availableSessions?: Array<{
    id?: number;
    name: string;
    schoolId?: number;
    isActive?: boolean;
  }>;
  availableSubjects?: Array<{
    id?: number;
    name: string;
    subjectCode: string;
    className: string;
    schoolId: number;
  }>;
}> = ({ open, onClose, onSubmit, initialData, isEdit, schoolData, selectedClass, availableClasses, availableSessions, availableSubjects }) => {
  const { t } = useTranslation();
  const { isSchoolAdmin, isTeacher, user } = useAuth();


  const schoolId = selectedClass?.schoolId ||
    (availableClasses && availableClasses.length > 0 ? availableClasses[0].schoolId : 0) ||
    (schoolData?.schoolCode ? (availableClasses?.[0]?.schoolId || 0) : 0);

  const { subjectsQuery } = useSubjects(
    false,
    schoolId || undefined
  );

  const allSubjects = subjectsQuery.data || [];
  const subjectsLoading = subjectsQuery.isLoading;

  // State for selected admission class (for filtering subjects)
  const [selectedAdmissionClass, setSelectedAdmissionClass] = useState<string>('');
  // State for selected class ID (to uniquely identify classes with same name but different sections)
  const [selectedClassId, setSelectedClassId] = useState<number | undefined>(
    initialData?.classId || selectedClass?.id || undefined
  );
  // State for selected session ID (for filtering subjects)
  const [selectedSessionId, setSelectedSessionId] = useState<number | undefined>(undefined);

  const [expandedGroups, setExpandedGroups] = useState<Set<string>>(new Set());
  const [expandedSubgroups, setExpandedSubgroups] = useState<Set<string>>(new Set());

  const { data: vocationalSubjects } = useVocationalSubjects(
    selectedClassId || null,
    false
  );

  // Validation errors state
  const [validationErrors, setValidationErrors] = useState<Record<string, string>>({});

  // New state for Class 11th/12th subject selection rules
  const [selectedMainGroup, setSelectedMainGroup] = useState<string | null>(null); // Selected main group (only one)
  const [languageGroupExpanded, setLanguageGroupExpanded] = useState<boolean>(true); // Language group expanded by default
  const [selectedStream, setSelectedStream] = useState<string>(''); // Selected stream (maps to group code 1-8)

  // Stream options mapping (Group Code -> Group Name)
  const streamOptions = [
    { value: '1', label: 'HUMANITIES GROUP', groupName: 'HUMANITIES GROUP' },
    { value: '2', label: 'SCIENCE MATHS GROUP', groupName: 'SCIENCE MATHS GROUP' },
    { value: '3', label: 'COMMERCE GROUP', groupName: 'COMMERCE GROUP' },
    { value: '4', label: 'AGRICULTURE GROUP', groupName: 'AGRICULTURE GROUP' },
    { value: '5', label: 'FINE ART GROUP', groupName: 'FINE ART GROUP' },
    { value: '6', label: 'HOME SCIENCE GROUP', groupName: 'HOME SCIENCE GROUP' },
    { value: '7', label: 'SCIENCE BIO. GROUP', groupName: 'SCIENCE BIO. GROUP' },
    { value: '8', label: 'LANGUAGE GROUP', groupName: 'LANGUAGE GROUP' },
  ];

  // Get photo info when editing (only if we have an ID)
  const { data: applicationPhotoInfo, isLoading: applicationPhotoLoading } = useApplicationPhotoInfo(
    isEdit && initialData?.id ? initialData.id : undefined
  );

  // Check if student has marksheets (to disable subject editing)
  const { useMarksheetByStudentId } = useMarkSheets();
  const { data: existingMarksheets } = useMarksheetByStudentId(
    isEdit && initialData?.id ? initialData.id : null,
    isEdit && !!initialData?.id
  );
  const hasMarksheets = existingMarksheets && existingMarksheets.length > 0;

  // Helper function to check if a field should be read-only
  const isFieldReadOnly = useCallback((fieldName: string): boolean => {
    if (fieldName === 'subjects') {
      return !!hasMarksheets;
    }
    return false;
  }, [hasMarksheets]);

  // Check if class is 11th or 12th
  const isEleventhTwelfthClass = React.useMemo(() => {
    if (!selectedAdmissionClass) return false;
    const classNumber = selectedAdmissionClass.match(/\d+/)?.[0];
    return classNumber === '11' || classNumber === '12';
  }, [selectedAdmissionClass]);

  // Helper function to extract subject code from "GROUP|CODE" format
  const extractSubjectCode = useCallback((storedValue: string): string => {
    if (!storedValue) return '';
    if (storedValue.includes('|')) {
      return storedValue.split('|')[1];
    }
    return storedValue;
  }, []);

  // Helper function to identify language subjects
  const isLanguageSubject = useCallback((subjectCode: string, subjectName?: string): boolean => {
    // Check by code
    const langCodes = ["051", "052", "053", "054", "055"];
    if (langCodes.includes(subjectCode)) return true;

    // Check by name if available
    if (subjectName) {
      const normalizedName = subjectName.toLowerCase();
      const languageKeywords = ['hindi', 'english', 'marathi', 'gujarati', 'sanskrit', 'urdu', 'bengali', 'telugu', 'tamil', 'kannada', 'malayalam', 'punjabi', 'odia'];
      return languageKeywords.some(keyword => normalizedName.includes(keyword));
    }
    return false;
  }, []);

  // Helper function to check if a subject belongs to Additional Subject group
  const isAdditionalSubject = useCallback((subjectCode: string, subjectName?: string): boolean => {
    if (!isEleventhTwelfthClass) return false;
    const additionalGroup = ELEVENTH_TWELFTH_CLASS_SUBJECTS_GROUPED.find(g => g.groupName === "ADDITIONAL SUBJECT (OPTIONAL)");
    if (!additionalGroup) return false;
    return additionalGroup.subjects.some(s => s.code === subjectCode);
  }, [isEleventhTwelfthClass]);

  // Helper function to check if a subject belongs to NSQF Subject group
  const isNSQFSubject = useCallback((subjectCode: string, subjectName?: string): boolean => {
    if (!isEleventhTwelfthClass) return false;
    const nsqfGroup = ELEVENTH_TWELFTH_CLASS_SUBJECTS_GROUPED.find(g => g.groupName === "NSQF SUBJECT (OPTIONAL)");
    if (!nsqfGroup) return false;
    return nsqfGroup.subjects.some(s => s.code === subjectCode);
  }, [isEleventhTwelfthClass]);

  // Helper function to get main group name for a subject
  const getMainGroupName = useCallback((subjectCode: string, subjectName?: string): string | null => {
    if (!isEleventhTwelfthClass) return null;

    if (isLanguageSubject(subjectCode, subjectName)) {
      return null;
    }

    const mainGroups = [
      "HUMANITIES GROUP",
      "SCIENCE MATHS GROUP",
      "COMMERCE GROUP",
      "AGRICULTURE GROUP",
      "FINE ART GROUP",
      "HOME SCIENCE GROUP",
      "SCIENCE BIO. GROUP",
      "LANGUAGE GROUP"
    ];

    for (const groupName of mainGroups) {
      const group = ELEVENTH_TWELFTH_CLASS_SUBJECTS_GROUPED.find(g => g.groupName === groupName);
      if (group) {
        if (group.subjects.some(s => s.code === subjectCode || (subjectName && s.name === subjectName))) {
          return groupName;
        }
        if (group.hasSubgroups && group.subgroups) {
          for (const subgroup of group.subgroups) {
            if (subgroup.subjects.some(s => s.code === subjectCode || (subjectName && s.name === subjectName))) {
              return groupName;
            }
          }
        }
      }
    }
    return null;
  }, [isEleventhTwelfthClass, isLanguageSubject]);

  // Use the application photo info
  const photoInfo = applicationPhotoInfo;
  const photoLoading = applicationPhotoLoading;

  // State for authenticated image URL
  const [authenticatedImageUrl, setAuthenticatedImageUrl] = useState<string | null>(null);
  const [imagePreviewModal, setImagePreviewModal] = useState<boolean>(false);

  // State for authenticated signature URL
  const [authenticatedSignatureUrl, setAuthenticatedSignatureUrl] = useState<string | null>(null);
  const [signaturePreviewModal, setSignaturePreviewModal] = useState<boolean>(false);

  // Extract signature info: First check if initialData has studentSignature as a filename (string)
  // If not, fall back to applicationPhotoInfo from API
  const signatureFileName = (typeof initialData?.studentSignature === 'string' && initialData.studentSignature)
    ? initialData.studentSignature
    : (applicationPhotoInfo?.studentSignature || null);

  // Debug logging for photo info and create authenticated image URL
  useEffect(() => {
    // First check if initialData has studentPhoto as a filename (string)
    // If not, fall back to photoInfo from API
    const photoFileName = (typeof initialData?.studentPhoto === 'string' && initialData.studentPhoto)
      ? initialData.studentPhoto
      : (photoInfo?.fileName || null);

    if (photoFileName && isEdit && open) {
      // Use application form photo endpoint
      const constructedUrl = `${process.env.REACT_APP_API_URL}/application-forms/photo/${photoFileName}`;

      // Create authenticated image URL with token
      const token = getAuthToken();
      if (token) {
        // Create a blob URL with authenticated request
        fetch(constructedUrl, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'X-Tenant-ID': String(axios.defaults.headers.common['X-Tenant-ID'] || '')
          }
        })
          .then(response => {
            if (response.ok) {
              return response.blob();
            } else {
              throw new Error(`HTTP ${response.status}`);
            }
          })
          .then(blob => {
            const imageUrl = URL.createObjectURL(blob);
            setAuthenticatedImageUrl(imageUrl);
          })
          .catch(error => {
            setAuthenticatedImageUrl(null);
          });
      } else {
        setAuthenticatedImageUrl(null);
      }
    } else if (isEdit && open && !photoFileName) {
      // If no photoFileName (null), clear the image URL
      setAuthenticatedImageUrl(null);
    } else if (!isEdit || !open) {
      // Clear image URL when not editing or modal is closed
      setAuthenticatedImageUrl(null);
    }
  }, [initialData?.studentPhoto, photoInfo, isEdit, open]);

  // Debug logging for signature info and create authenticated signature URL
  useEffect(() => {
    // Only proceed if we have a signature fileName (can be null if no signature exists)
    if (signatureFileName && isEdit && open) {
      // Use application form photo endpoint
      const constructedUrl = `${process.env.REACT_APP_API_URL}/application-forms/photo/${signatureFileName}`;

      // Create authenticated signature URL with token
      const token = getAuthToken();
      if (token) {
        // Create a blob URL with authenticated request
        fetch(constructedUrl, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'X-Tenant-ID': String(axios.defaults.headers.common['X-Tenant-ID'] || '')
          }
        })
          .then(response => {

            // Handle 404 gracefully - application might not have a signature
            if (response.status === 404) {
              setAuthenticatedSignatureUrl(null);
              return null;
            }

            // Handle 401/403 gracefully - don't trigger logout, just show no signature
            if (response.status === 401 || response.status === 403) {
              setAuthenticatedSignatureUrl(null);
              return null;
            }

            if (response.ok) {
              return response.blob();
            } else {
              setAuthenticatedSignatureUrl(null);
              return null;
            }
          })
          .then(blob => {
            // Only create blob URL if we got a valid blob
            if (blob) {
              const signatureUrl = URL.createObjectURL(blob);
              setAuthenticatedSignatureUrl(signatureUrl);
            }
          })
          .catch(error => {
            // Silently handle errors - don't trigger logout
            setAuthenticatedSignatureUrl(null);
          });
      } else {
        setAuthenticatedSignatureUrl(null);
      }
    } else if (isEdit && open && !signatureFileName) {
      // If no signatureFileName (null), clear the signature URL
      setAuthenticatedSignatureUrl(null);
    } else if (!isEdit || !open) {
      // Clear signature URL when not editing or modal is closed
      setAuthenticatedSignatureUrl(null);
    }
  }, [signatureFileName, initialData?.studentSignature, isEdit, open]);

  // Cleanup blob URL on unmount or when switching between different applications
  useEffect(() => {
    return () => {
      if (authenticatedImageUrl) {
        URL.revokeObjectURL(authenticatedImageUrl);
      }
      if (authenticatedSignatureUrl) {
        URL.revokeObjectURL(authenticatedSignatureUrl);
      }
    };
  }, [initialData?.id]); // Clean up when switching between different applications

  // Helper function to normalize subjects to always be an array
  const normalizeSubjects = useCallback((subjects: string[] | string | undefined): string[] => {
    if (!subjects) return [];
    if (Array.isArray(subjects)) return subjects;
    if (typeof subjects === 'string') return subjects.split(',').map(s => s.trim()).filter(Boolean);
    return [];
  }, []);

  // Helper function to safely parse date strings and arrays (including dd/MM/yyyy format and array format)
  const safeParseDateString = useCallback((dateInput: string | number[] | null | undefined): Date | null => {
    if (!dateInput) return null;

    try {
      // Handle array format dates [year, month, day] - common from APIs
      if (Array.isArray(dateInput) && dateInput.length >= 3) {
        const [year, month, day] = dateInput;
        if (typeof year === 'number' && typeof month === 'number' && typeof day === 'number') {
          // Note: Backend sends month as 1-indexed (1=Jan, 12=Dec), so we use it directly
          const date = new Date(year, month - 1, day);
          if (isValid(date)) {
            return date;
          }
        }
      }

      // Handle string format dates
      if (typeof dateInput === 'string') {
        // First try to parse as dd/MM/yyyy format (as expected by backend)
        if (dateInput.match(/^\d{2}\/\d{2}\/\d{4}$/)) {
          const [day, month, year] = dateInput.split('/');
          const date = new Date(parseInt(year), parseInt(month) - 1, parseInt(day));
          if (isValid(date)) {
            return date;
          }
        }

        // Try to parse as ISO date (YYYY-MM-DD)
        const isoDate = parseISO(dateInput);
        if (isValid(isoDate)) {
          return isoDate;
        }

        // If ISO parsing fails, try to create a date directly
        const directDate = new Date(dateInput);
        if (isValid(directDate)) {
          return directDate;
        }
      }

      return null;
    } catch (error) {
      return null;
    }
  }, []);

  const [formData, setFormData] = useState<AdmissionFromData>(initialData ? {
    ...initialData,
    studentCode: initialData.studentCode || '',
    subjects: normalizeSubjects(initialData.subjects),
    isRegistered: typeof initialData.isRegistered === 'boolean'
      ? (initialData.isRegistered ? 'Approved' : 'Pending')
      : initialData.isRegistered,
    schoolId: initialData.schoolId || 0,
    sessionId: initialData.sessionId || 0,
  } : {
    applicationNo: '',
    rollNo: '',
    enrolmentNo: '',
    scholarNo: '',
    sssmid: '',
    penNo: '',
    apparId: '',
    studentCode: '',
    studentName: '',
    fatherName: '',
    motherName: '',
    dateOfBirth: '',
    gender: '',
    religion: '',
    caste: '',
    subCaste: '',
    casteCertificate: '',
    aadharNo: '',
    studentPhoto: null, // Initialize photo as null
    studentSignature: null, // Initialize signature as null
    houseNo: '',
    street: '',
    landmark: '',
    area: '',
    city: '',
    postOffice: '',
    district: '',
    state: '',
    pinCode: '',
    mobileNo: '',
    gmail: '',
    bankName: '',
    ifcCode: '',
    accountNo: '',
    annualIncome: '',
    admissionDate: '',
    admissionClass: selectedClass?.name || '', // Auto-populate from selected class
    section: selectedClass?.section || '', // Auto-populate from selected section
    medium: '',
    admissionType: '',
    stream: '', // Initialize stream as empty string
    cwsnCode: '',
    sambalKramank: '',
    subjects: [], // Initialize as empty array
    schoolId: 0,
    isRegistered: "Pending",
    sessionId: 0,
  });
  // REMOVED: This useEffect was causing form data to reset when props like availableClasses changed
  // Form initialization is now handled only in the main useEffect below (around line 828)
  // This prevents data loss when user is actively filling the form
  // The dependencies [open, initialData, selectedClass, normalizeSubjects, availableClasses, schoolData]
  // were causing the form to reset every time availableClasses query refetched

  // State for image preview
  const [imagePreview, setImagePreview] = useState<string | null>(null);
  const [imageError, setImageError] = useState<string>('');

  // State for signature preview
  const [signaturePreview, setSignaturePreview] = useState<string | null>(null);
  const [signatureError, setSignatureError] = useState<string>('');

  // Track previous class and session IDs to detect changes
  const prevClassIdRef = useRef<number | undefined>(undefined);
  const prevSessionIdRef = useRef<number | undefined>(undefined);

  // Update selectedAdmissionClass, selectedClassId, and selectedSessionId when formData changes
  useEffect(() => {
    if (formData.admissionClass) {
      setSelectedAdmissionClass(formData.admissionClass);
      // Find the class ID from availableClasses if we have the class name
      if (formData.classId) {
        setSelectedClassId(formData.classId);
      } else if (availableClasses) {
        const matchingClass = availableClasses.find(cls =>
          cls.name === formData.admissionClass &&
          (cls.section === formData.section || !formData.section)
        );
        if (matchingClass?.id) {
          setSelectedClassId(matchingClass.id);
        }
      }
    }
    if (formData.sessionId) {
      setSelectedSessionId(formData.sessionId);
    }
  }, [formData.admissionClass, formData.classId, formData.section, formData.sessionId, availableClasses]);

  // REMOVED: Aggressive subject clearing logic was causing subjects to disappear on initial load.
  // Instead, rely on validation to ensure valid subjects are selected for the current class.
  useEffect(() => {
    const currentClassId = selectedClassId;
    const currentSessionId = selectedSessionId || formData.sessionId;
    // Update refs for next comparison
    prevClassIdRef.current = currentClassId;
    prevSessionIdRef.current = currentSessionId;
  }, [selectedClassId, selectedSessionId, formData.sessionId]);

  // Filter classes by selected session
  const filteredClasses = React.useMemo(() => {
    if (!availableClasses) return [];

    // If no session selected, return empty array to disable class selection
    const currentSessionId = selectedSessionId || formData.sessionId;
    if (!currentSessionId || currentSessionId === 0) {
      return [];
    }

    // Filter classes by sessionId
    return availableClasses.filter((cls) => {
      const classSessionId = cls.sessionId;

      // If class has no sessionId, include it (for backward compatibility)
      if (classSessionId === null || classSessionId === undefined) {
        return true;
      }

      // Compare normalized IDs (handle number/string conversion)
      return Number(classSessionId) === Number(currentSessionId);
    });
  }, [availableClasses, selectedSessionId, formData.sessionId]);

  // Filter subjects by selected admission class and session
  const classSubjects = React.useMemo(() => {
    if (!allSubjects || allSubjects.length === 0) return [];
    if (!selectedAdmissionClass && !selectedClassId) return [];

    // Find the selected class data by ID first, then fallback to name
    let selectedClassData: {
      id?: number;
      name: string;
      section: string;
      schoolId: number;
      sessionId?: number;
    } | undefined = undefined;
    if (selectedClassId) {
      selectedClassData = availableClasses?.find(cls => cls.id === selectedClassId);
    }
    if (!selectedClassData && selectedAdmissionClass) {
      selectedClassData = availableClasses?.find(cls =>
        cls.name === selectedAdmissionClass ||
        cls.name === `${selectedAdmissionClass} - ${formData.section}` ||
        `${cls.name} - ${cls.section}` === selectedAdmissionClass
      );
    }

    // Get the section from formData or selectedClassData
    const section = formData.section || selectedClassData?.section || '';

    // Use the selected class's schoolId if available, otherwise fall back to the general schoolId
    const effectiveSchoolId = selectedClassData?.schoolId || schoolId;

    // Filter by class name, school ID, and session (if session is selected)
    const filtered = allSubjects.filter((subject: any) => {
      // Match class name - more flexible matching like ApplicationFormModal
      // Handle various formats: "11th", "11th - A", "11", etc.
      const normalizedSelectedClass = selectedAdmissionClass?.trim();
      const normalizedSubjectClass = subject.className?.trim();

      if (!normalizedSubjectClass || !normalizedSelectedClass) {
        return false;
      }

      // Extract class numbers for comparison (e.g., "11" from "11th" or "11th - A")
      const selectedClassNum = normalizedSelectedClass.match(/\d+/)?.[0];
      const subjectClassNum = normalizedSubjectClass.match(/\d+/)?.[0];

      // Multiple matching strategies
      const matchesClass =
        // Exact match
        normalizedSubjectClass === normalizedSelectedClass ||
        // Match with section
        normalizedSubjectClass === `${normalizedSelectedClass} - ${section}` ||
        // Match class name with section format
        normalizedSubjectClass === `${normalizedSelectedClass}${section ? ' - ' + section : ''}` ||
        // Match if class numbers are the same (handles "11" vs "11th")
        (selectedClassNum && subjectClassNum && selectedClassNum === subjectClassNum) ||
        // Match if one contains the other (after removing section)
        normalizedSubjectClass.split(' - ')[0] === normalizedSelectedClass.split(' - ')[0] ||
        // Match if subject class includes selected class (or vice versa)
        normalizedSubjectClass.includes(normalizedSelectedClass) ||
        normalizedSelectedClass.includes(normalizedSubjectClass.split(' - ')[0]);

      // Match school ID - use effectiveSchoolId (from selected class if available)
      // Only filter by school if we have a valid schoolId (not 0 or undefined)
      const matchesSchool = !effectiveSchoolId || effectiveSchoolId === 0 || !subject.schoolId || subject.schoolId === effectiveSchoolId;

      // Match session if both session is selected and class has session info
      // But don't filter by session if subjects don't have session info (for backward compatibility)
      let matchesSession = true;
      if (selectedSessionId && selectedClassData?.sessionId) {
        // Only filter by session if the subject also has session info
        // If subject doesn't have sessionId, include it (backward compatibility)
        if (subject.sessionId !== null && subject.sessionId !== undefined) {
          matchesSession = Number(subject.sessionId) === Number(selectedSessionId);
        }
        // If subject has sessionId but it's null/undefined, still include it for backward compatibility
      }

      return matchesClass && matchesSchool && matchesSession;
    });

    return filtered;
  }, [allSubjects, selectedAdmissionClass, selectedClassId, selectedSessionId, schoolId, availableClasses, formData.section]);

  // Image handling functions
  const handleImageChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      // Validate file type
      if (!file.type.startsWith('image/')) {
        setImageError(t("please_select_valid_image_file"));
        return;
      }

      // Validate file size (max 5MB)
      if (file.size > 5 * 1024 * 1024) {
        setImageError(t("image_size_should_be_less_than_5mb"));
        return;
      }

      setImageError('');
      setFormData(prev => ({ ...prev, studentPhoto: file }));

      // Create preview
      const reader = new FileReader();
      reader.onload = (e) => {
        setImagePreview(e.target?.result as string);
      };
      reader.readAsDataURL(file);
    }
  }, []);

  const handleRemoveImage = useCallback(() => {
    setFormData(prev => ({ ...prev, studentPhoto: null, studentPhotoUrl: undefined }));
    setImagePreview(null);
    setImageError('');
    setAuthenticatedImageUrl(null);
  }, []);

  // Signature handling functions
  const handleSignatureChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      // Validate file type
      if (!file.type.startsWith('image/')) {
        setSignatureError(t("please_select_valid_image_file"));
        return;
      }

      // Validate file size (max 5MB)
      if (file.size > 5 * 1024 * 1024) {
        setSignatureError(t("image_size_should_be_less_than_5mb"));
        return;
      }

      setSignatureError('');
      setFormData(prev => ({ ...prev, studentSignature: file }));

      // Create preview
      const reader = new FileReader();
      reader.onload = (e) => {
        setSignaturePreview(e.target?.result as string);
      };
      reader.readAsDataURL(file);
    }
  }, []);

  const handleRemoveSignature = useCallback(() => {
    setFormData(prev => ({ ...prev, studentSignature: null, studentSignatureUrl: undefined }));
    setSignaturePreview(null);
    setSignatureError('');
    setAuthenticatedSignatureUrl(null);
  }, []);

  // Function to get available medium options based on school's medium
  const getAvailableMediumOptions = useCallback(() => {
    const options: { value: string; label: string }[] = [];

    // Always include the current form data's medium if it exists (for editing)
    if (formData.medium && !options.find(opt => opt.value === formData.medium)) {
      options.push({
        value: formData.medium,
        label: formData.medium
      });
    }

    if (!schoolData?.medium) {
      // If no school data, return current medium or default options
      return options.length > 0 ? options : [
        { value: "English", label: "English" },
        { value: "Hindi", label: "Hindi" }
      ];
    }

    const schoolMedium = schoolData.medium;

    // Handle array of mediums (new backend format)
    if (Array.isArray(schoolMedium)) {
      schoolMedium.forEach(medium => {
        if (!options.find(opt => opt.value === medium)) {
          options.push({
            value: medium,
            label: medium
          });
        }
      });
    }

    // Handle string format (legacy support)
    if (typeof schoolMedium === 'string') {
      // If school medium is "ENGLISH & HINDI", show both options
      if (schoolMedium === "ENGLISH & HINDI") {
        if (!options.find(opt => opt.value === "English")) {
          options.push({ value: "English", label: "English" });
        }
        if (!options.find(opt => opt.value === "Hindi")) {
          options.push({ value: "Hindi", label: "Hindi" });
        }
      }
      // If school medium is "ENGLISH", show only English
      else if (schoolMedium === "ENGLISH") {
        if (!options.find(opt => opt.value === "English")) {
          options.push({ value: "English", label: "English" });
        }
      }
      // If school medium is "HINDI", show only Hindi
      else if (schoolMedium === "HINDI") {
        if (!options.find(opt => opt.value === "Hindi")) {
          options.push({ value: "Hindi", label: "Hindi" });
        }
      }
      // Handle comma-separated mediums like "Hindi, English"
      else if (schoolMedium.includes(',')) {
        const mediums = schoolMedium.split(',').map(m => m.trim());
        mediums.forEach(medium => {
          if (medium && !options.find(opt => opt.value === medium)) {
            options.push({ value: medium, label: medium });
          }
        });
      }
      // Single medium string
      else {
        if (!options.find(opt => opt.value === schoolMedium)) {
          options.push({ value: schoolMedium, label: schoolMedium });
        }
      }
    }

    // If no options found, add default options
    if (options.length === 0) {
      options.push(
        { value: "English", label: "English" },
        { value: "Hindi", label: "Hindi" }
      );
    }

    return options;
  }, [schoolData?.medium, formData.medium]);

  // Function to get default medium based on school's medium
  const getDefaultMedium = useCallback(() => {
    // Always return empty string to let user choose from dropdown
    // This ensures the dropdown is always shown with all available options
    return "";
  }, []);

  // Track if form has been initialized to prevent overwriting user input
  const formInitializedRef = useRef<boolean>(false);
  const previousInitialDataIdRef = useRef<number | undefined>(undefined);
  const previousInitialDataRef = useRef<any>(undefined);

  // Track previous open state to detect when modal opens
  const previousOpenRef = useRef<boolean>(false);

  // Update form data when initialData changes or modal opens
  useEffect(() => {
    // Only run this effect when modal opens (open changes from false to true) or when initialData ID actually changes
    // Don't run when functions or other props change to prevent form reset

    const modalJustOpened = !previousOpenRef.current && open;
    const initialDataIdChanged = previousInitialDataIdRef.current !== initialData?.id;

    // Only initialize form when modal just opened or when initialData ID actually changes
    // IMPORTANT: Always initialize when initialData is provided (includes draft data)
    // Prevent re-initialization only when form already has user-entered data AND no initialData provided

    // Check if initialData is provided (could be draft data or existing record)
    const hasInitialData = initialData && (
      initialData.id ||
      initialData.studentName ||
      initialData.fatherName ||
      initialData.mobileNo ||
      initialData.admissionClass
    );

    // Check if form has user-entered data (only relevant when no initialData is provided)
    const hasUserEnteredData = !hasInitialData && formData && (
      formData.studentName?.trim() ||
      formData.fatherName?.trim() ||
      formData.motherName?.trim() ||
      formData.mobileNo?.trim() ||
      formData.dateOfBirth?.trim()
    );

    // Initialize if:
    // 1. Modal just opened AND form not initialized AND (initialData provided OR no user data OR switching records)
    // 2. OR switching to different record (always initialize)
    // 3. OR initialData provided (draft data or existing record) - always initialize
    const shouldInitialize = open && !formInitializedRef.current && (
      (modalJustOpened && (hasInitialData || !hasUserEnteredData || initialDataIdChanged)) ||
      initialDataIdChanged ||
      (hasInitialData && modalJustOpened)
    );

    // Update previous open state
    previousOpenRef.current = open;

    if (shouldInitialize) {
      if (initialData && (initialData.id || initialData.studentName || initialData.fatherName || initialData.mobileNo || (initialData.subjects && initialData.subjects.length > 0))) {
        // This is either editing an existing form OR restoring draft data
        // Transform gender data to ensure proper mapping
        const transformGender = (gender: string | null | undefined): string => {
          if (!gender) return '';
          const genderLower = gender.toLowerCase().trim();
          if (genderLower === 'm' || genderLower === 'male') return 'M';
          if (genderLower === 'f' || genderLower === 'female') return 'F';
          if (genderLower === 'o' || genderLower === 'other') return 'O';
          return gender; // Return original value if not recognized
        };

        // Convert any existing dates to proper format (no defaults)
        let dateOfBirth = '';
        let admissionDate = '';

        if (initialData.dateOfBirth) {
          const parsedDate = safeParseDateString(initialData.dateOfBirth);
          if (parsedDate && isValid(parsedDate)) {
            dateOfBirth = format(parsedDate, 'dd/MM/yyyy');
          }
        }

        if (initialData.admissionDate) {
          const parsedDate = safeParseDateString(initialData.admissionDate);
          if (parsedDate && isValid(parsedDate)) {
            admissionDate = format(parsedDate, 'dd/MM/yyyy');
          }
        }

        // Preserve all draft/initial data fields
        // For draft data, preserve ALL fields; for existing records, preserve all fields too
        const updatedFormData: AdmissionFromData = {
          ...initialData,
          // Ensure all required fields are present with proper defaults
          applicationNo: initialData.applicationNo || '',
          rollNo: initialData.rollNo || '',
          enrolmentNo: initialData.enrolmentNo || '',
          scholarNo: initialData.scholarNo || '',
          sssmid: initialData.sssmid || '',
          penNo: initialData.penNo || '',
          apparId: initialData.apparId || '',
          studentCode: initialData.studentCode || '',
          studentName: initialData.studentName || '',
          fatherName: initialData.fatherName || '',
          motherName: initialData.motherName || '',
          dateOfBirth: dateOfBirth || initialData.dateOfBirth || '', // Use parsed date or original
          gender: initialData.gender ? transformGender(initialData.gender) : '', // Transform gender for proper mapping
          religion: initialData.religion || '',
          caste: initialData.caste || '',
          subCaste: initialData.subCaste || '',
          casteCertificate: initialData.casteCertificate || '',
          aadharNo: initialData.aadharNo || '',
          studentPhoto: null, // Reset photo for editing (will need to be re-uploaded)
          studentSignature: null, // Reset signature for editing (will need to be re-uploaded)
          studentPhotoUrl: initialData.studentPhotoUrl, // Preserve photo URL if exists
          studentSignatureUrl: initialData.studentSignatureUrl, // Preserve signature URL if exists
          houseNo: initialData.houseNo || '',
          street: initialData.street || '',
          landmark: initialData.landmark || '',
          area: initialData.area || '',
          city: initialData.city || '',
          postOffice: initialData.postOffice || '',
          district: initialData.district || '',
          state: initialData.state || '',
          pinCode: initialData.pinCode || '',
          mobileNo: initialData.mobileNo || user?.email || '', // Preserve mobileNo or use user.email
          gmail: initialData.gmail || '',
          bankName: initialData.bankName || '',
          ifcCode: initialData.ifcCode || '',
          accountNo: initialData.accountNo || '',
          annualIncome: initialData.annualIncome || '',
          admissionDate: admissionDate || initialData.admissionDate || '', // Use parsed date or original
          admissionClass: initialData.admissionClass || '',
          section: initialData.section || '',
          medium: initialData.medium || '',
          admissionType: initialData.admissionType || '',
          stream: initialData.stream || '', // Initialize stream as empty string
          cwsnCode: initialData.cwsnCode || '',
          sambalKramank: initialData.sambalKramank || '',
          subjects: normalizeSubjects(initialData.subjects), // Normalize subjects to array
          schoolId: initialData.schoolId || 0, // Preserve schoolId
          classId: initialData.classId, // Preserve classId
          sessionId: initialData.sessionId || 0, // Preserve sessionId
          sessionName: initialData.sessionName || '', // Preserve sessionName
          isRegistered: typeof initialData.isRegistered === 'boolean'
            ? (initialData.isRegistered ? 'Approved' : 'Pending')
            : (initialData.isRegistered || 'Pending'),
        };

        setFormData(updatedFormData as AdmissionFromData);

        // Clear validation errors when loading existing data
        setValidationErrors({});

        // Set selected class ID if available
        if (initialData.classId) {
          setSelectedClassId(initialData.classId);
        } else if (initialData.admissionClass && availableClasses) {
          // Try to find the class by name and section
          const matchingClass = availableClasses.find(cls =>
            cls.name === initialData.admissionClass &&
            (cls.section === initialData.section || !initialData.section)
          );
          if (matchingClass?.id) {
            setSelectedClassId(matchingClass.id);
          }
        }

        formInitializedRef.current = true;
        previousInitialDataIdRef.current = initialData.id;
        // Store a reference to the initialData object for comparison
        previousInitialDataRef.current = initialData;
      } else {
        // Get default medium based on school's medium
        const defaultMedium = getDefaultMedium();

        // Get student name and mobile number from initialData or getStudentData()
        const studentData = getStudentData();
        // Safely extract values from initialData if it exists
        const studentNameFromInitial = initialData ? (initialData as any).studentName : undefined;
        const mobileNoFromInitial = initialData ? (initialData as any).mobileNo : undefined;
        const schoolIdFromInitial = initialData ? (initialData as any).schoolId : undefined;
        const sessionIdFromInitial = initialData ? (initialData as any).sessionId : undefined;

        const studentName = studentNameFromInitial || studentData?.studentName || studentData?.name || '';
        // Use user.email for mobile number (user.email contains the phone number in this system)
        const mobileNo = mobileNoFromInitial || user?.email || studentData?.mobileNo || studentData?.mobileNumber || '';
        const schoolId = schoolIdFromInitial || 0;
        const sessionId = sessionIdFromInitial || 0;

        // Reset to default values when adding new form, preserving only studentName and mobileNo
        setFormData({
          applicationNo: '',
          rollNo: '',
          enrolmentNo: '',
          scholarNo: '',
          sssmid: '',
          penNo: '',
          apparId: '',
          studentCode: '',
          studentName: studentName,
          fatherName: '',
          motherName: '',
          dateOfBirth: '',
          gender: '',
          religion: '',
          caste: '',
          subCaste: '',
          casteCertificate: '',
          aadharNo: '',
          studentPhoto: null, // Reset photo
          studentPhotoUrl: undefined, // Reset photo URL
          studentSignature: null, // Reset signature
          studentSignatureUrl: undefined, // Reset signature URL
          houseNo: '',
          street: '',
          landmark: '',
          area: '',
          city: '',
          postOffice: '',
          district: '',
          state: '',
          pinCode: '',
          mobileNo: mobileNo,
          gmail: '',
          bankName: '',
          ifcCode: '',
          accountNo: '',
          annualIncome: '',
          admissionDate: '',
          admissionClass: '', // Will be selected from dropdown
          section: '', // Will be auto-populated based on selected class
          medium: defaultMedium, // Set default medium based on school's medium
          admissionType: '',
          stream: '', // Initialize stream as empty string
          cwsnCode: '',
          sambalKramank: '',
          subjects: [], // Initialize as empty array
          schoolId: schoolId,
          isRegistered: "Pending",
          sessionId: sessionId,
        } as AdmissionFromData);

        // Set selected class ID if selectedClass is provided
        if (selectedClass?.id) {
          setSelectedClassId(selectedClass.id);
        } else {
          setSelectedClassId(undefined);
        }

        formInitializedRef.current = true;
        previousInitialDataIdRef.current = undefined;
        previousInitialDataRef.current = undefined;
      }
    }
  }, [open, initialData?.id]); // Only depend on open state and initialData ID

  // Separate effect to reset form when modal closes
  useEffect(() => {
    if (!open) {
      // Reset initialization flag when modal closes
      formInitializedRef.current = false;
      previousInitialDataIdRef.current = undefined;
      previousInitialDataRef.current = undefined;

      // Reset form data when modal closes (unless we're editing an existing application)
      // Only preserve studentName and mobileNo for new forms
      if (!isEdit && !initialData?.id) {
        const studentData = getStudentData();
        const studentName = studentData?.studentName || studentData?.name || '';
        // Use user.email for mobile number (user.email contains the phone number in this system)
        const mobileNo = user?.email || studentData?.mobileNo || studentData?.mobileNumber || '';

        // Get default medium based on school's medium
        const defaultMedium = getDefaultMedium();

        // Reset to default values, keeping only studentName and mobileNo
        setFormData({
          applicationNo: '',
          rollNo: '',
          enrolmentNo: '',
          scholarNo: '',
          sssmid: '',
          penNo: '',
          apparId: '',
          studentCode: '',
          studentName: studentName,
          fatherName: '',
          motherName: '',
          dateOfBirth: '',
          gender: '',
          religion: '',
          caste: '',
          subCaste: '',
          casteCertificate: '',
          aadharNo: '',
          studentPhoto: null,
          studentPhotoUrl: undefined,
          studentSignature: null,
          studentSignatureUrl: undefined,
          houseNo: '',
          street: '',
          landmark: '',
          area: '',
          city: '',
          postOffice: '',
          district: '',
          state: '',
          pinCode: '',
          mobileNo: mobileNo,
          gmail: '',
          bankName: '',
          ifcCode: '',
          accountNo: '',
          annualIncome: '',
          admissionDate: '',
          admissionClass: '',
          section: '',
          medium: defaultMedium,
          admissionType: '',
          stream: '', // Initialize stream as empty string
          cwsnCode: '',
          sambalKramank: '',
          subjects: [],
          schoolId: 0,
          isRegistered: "Pending",
          sessionId: 0,
        });

        // Reset selected class ID
        setSelectedClassId(undefined);
        setSelectedAdmissionClass('');
      }
    }
  }, [open, isEdit, initialData?.id]); // Only reset when modal closes

  // Reset image preview and validation errors when modal opens for new forms or different applications
  useEffect(() => {
    if (open) {
      // Always reset photo state when modal opens
      setImagePreview(null);
      setImageError('');
      setAuthenticatedImageUrl(null);
      // Reset signature state when modal opens
      setSignaturePreview(null);
      // Clear validation errors when modal opens
      setValidationErrors({});
    }
  }, [open, initialData?.id]); // Reset when modal opens or when editing different application

  // Clear validation errors for fields that have valid values
  useEffect(() => {
    setValidationErrors(prevErrors => {
      if (Object.keys(prevErrors).length === 0) {
        return prevErrors; // No errors to clear
      }

      const updatedErrors = { ...prevErrors };
      let hasChanges = false;

      // Clear errors for fields that now have values
      if (formData.studentName?.trim() && updatedErrors.studentName) {
        delete updatedErrors.studentName;
        hasChanges = true;
      }
      if (formData.fatherName?.trim() && updatedErrors.fatherName) {
        delete updatedErrors.fatherName;
        hasChanges = true;
      }
      if (formData.motherName?.trim() && updatedErrors.motherName) {
        delete updatedErrors.motherName;
        hasChanges = true;
      }
      if (formData.gender?.trim() && updatedErrors.gender) {
        delete updatedErrors.gender;
        hasChanges = true;
      }
      if (formData.dateOfBirth?.trim() && updatedErrors.dateOfBirth) {
        delete updatedErrors.dateOfBirth;
        hasChanges = true;
      }
      // Clear mobile number error if it has value from formData or user.email
      if ((formData.mobileNo?.trim() || user?.email) && updatedErrors.mobileNo) {
        delete updatedErrors.mobileNo;
        hasChanges = true;
      }
      // Check both admissionClass and selectedClassId since the field uses selectedClassId
      // If either has a value, clear the error
      if ((formData.admissionClass?.trim() || selectedClassId) && updatedErrors.admissionClass) {
        delete updatedErrors.admissionClass;
        hasChanges = true;
      }
      if (formData.medium?.trim() && updatedErrors.medium) {
        delete updatedErrors.medium;
        hasChanges = true;
      }
      if (formData.sessionName?.trim() && updatedErrors.sessionName) {
        delete updatedErrors.sessionName;
        hasChanges = true;
      }

      return hasChanges ? updatedErrors : prevErrors;
    });
  }, [formData.studentName, formData.fatherName, formData.motherName, formData.gender, formData.dateOfBirth, formData.mobileNo, formData.admissionClass, selectedClassId, formData.medium, formData.sessionName, user?.email]);

  // Auto-fill mobile number from user.email when available (for new forms only)
  // This ensures mobileNo is set even if initialization didn't capture it
  useEffect(() => {
    if (open && user?.email && !isEdit && !initialData?.id) {
      // Only auto-fill for new forms when user.email is available
      setFormData(prev => {
        // Only update if mobileNo is empty or different from user.email
        if (!prev.mobileNo || prev.mobileNo !== user.email) {
          return {
            ...prev,
            mobileNo: user.email
          };
        }
        return prev;
      });
    }
  }, [open, user?.email, isEdit, initialData?.id]);

  // Additional effect to clear admissionClass error when selectedClassId changes
  useEffect(() => {
    if (selectedClassId && validationErrors.admissionClass) {
      setValidationErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors.admissionClass;
        return newErrors;
      });
    }
  }, [selectedClassId, validationErrors.admissionClass]);

  // Function to get available subjects for the selected class
  const getAvailableSubjects = useCallback(() => {

    if (!selectedAdmissionClass || !classSubjects) {
      return [];
    }

    // Convert API subjects to the format expected by the form
    const availableSubjects = classSubjects.map((subject: any) => ({
      value: subject.subjectCode, // e.g., "401", "100", "200"
      label: `(${subject.subjectCode}) ${subject.name}` // e.g., "(401) Hindi", "(100) Maths (Standard)"
    }));

    return availableSubjects;
  }, [selectedAdmissionClass, classSubjects]);

  // Function to get filtered subject options (excluding already selected ones)
  const getFilteredSubjectOptions = useCallback(() => {
    const availableSubjects = getAvailableSubjects();

    const filtered = availableSubjects.filter((subject: any) =>
      !formData.subjects.includes(subject.value) // Compare by value (code) not label
    );

    return filtered;
  }, [getAvailableSubjects, formData.subjects]);

  // Function to add a new subject
  const handleAddSubject = useCallback((subjectValue: string) => {
    // Check if maximum limit (6 subjects) is reached
    if (formData.subjects.length >= 6) {
      return; // Don't add if limit is reached
    }
    const subject = getAvailableSubjects().find((s: any) => s.value === subjectValue);
    if (subject && !formData.subjects.includes(subject.value)) {
      setFormData(prev => ({
        ...prev,
        subjects: [...prev.subjects, subject.value] // Store only the code
      }));
    }
  }, [getAvailableSubjects, formData.subjects]);


  // Function to get subject display name from code
  const getSubjectDisplayName = useCallback((storedValue: string) => {
    const subjectCode = extractSubjectCode(storedValue);

    // First try to find from API subjects (class-specific subjects)
    const apiSubject = getAvailableSubjects().find((s: any) => s.value === subjectCode);
    if (apiSubject) {
      return apiSubject.label;
    }

    // Fallback to SubjectOptions mapping if API doesn't have it
    const subjectOption = TENTH_CLASS_SUBJECTS.find((s) => s.code === subjectCode) ||
      ELEVENTH_TWELFTH_CLASS_SUBJECTS.find((s) => s.code === subjectCode);
    if (subjectOption) {
      return `${subjectOption.name} (${subjectOption.code})`;
    }

    // Fallback: try to extract from stored value
    return storedValue.includes('|') ? storedValue.split('|')[1] : storedValue;
  }, [getAvailableSubjects, extractSubjectCode]);

  // Function to get subject code for display
  const getSubjectCodeForDisplay = useCallback((fullSubjectName: string) => {
    if (!fullSubjectName) return '';
    const codeMatch = fullSubjectName.match(/\((\d+)\)/);
    return codeMatch ? codeMatch[1] : fullSubjectName;
  }, []);



  // Helper function to process a group and return its structure
  const processGroup = useCallback((group: any, classSubjects: any[]): any | null => {
    if (group.hasSubgroups && group.subgroups) {
      let allSubgroupsComplete = true;
      const subgroupsData: any[] = [];

      group.subgroups.forEach((subgroup: any) => {
        const allSubjectsInSubgroupExist = subgroup.subjects.every((subgroupSubject: any) =>
          classSubjects.some((cs: any) =>
            cs.subjectCode === subgroupSubject.code || cs.name === subgroupSubject.name
          )
        );

        if (allSubjectsInSubgroupExist) {
          const subgroupSubjects: any[] = [];
          subgroup.subjects.forEach((subgroupSubject: any) => {
            const availableSubject = classSubjects.find((cs: any) =>
              cs.subjectCode === subgroupSubject.code || cs.name === subgroupSubject.name
            );
            if (availableSubject) {
              const codeToUse = subgroupSubject.code || availableSubject.subjectCode;
              const uniqueValue = `${group.groupName}|${codeToUse}`;
              subgroupSubjects.push({
                value: uniqueValue,
                label: `(${subgroupSubject.code}) ${subgroupSubject.name}`,
                subjectName: availableSubject.name,
                subjectCode: codeToUse,
                originalCode: subgroupSubject.code,
                databaseCode: availableSubject.subjectCode,
                groupName: group.groupName
              });
            }
          });

          subgroupsData.push({
            subgroupName: subgroup.subgroupName,
            description: subgroup.description,
            subjects: subgroupSubjects
          });
        } else {
          allSubgroupsComplete = false;
        }
      });

      if (allSubgroupsComplete && subgroupsData.length > 0) {
        return {
          hasSubgroups: true,
          groupCode: group.groupCode,
          description: group.description,
          subgroups: subgroupsData,
          subjects: subgroupsData.flatMap((sg: any) => sg.subjects)
        };
      }
      return null;
    } else {
      const allSubjectsExist = group.subjects.every((groupSubject: any) =>
        classSubjects.some((cs: any) =>
          cs.subjectCode === groupSubject.code || cs.name === groupSubject.name
        )
      );

      if (allSubjectsExist) {
        const groupSubjects: any[] = [];
        group.subjects.forEach((groupSubject: any) => {
          const availableSubject = classSubjects.find((cs: any) =>
            cs.subjectCode === groupSubject.code || cs.name === groupSubject.name
          );
          if (availableSubject) {
            const codeToUse = groupSubject.code || availableSubject.subjectCode;
            const uniqueValue = `${group.groupName}|${codeToUse}`;
            groupSubjects.push({
              value: uniqueValue,
              label: `(${groupSubject.code}) ${groupSubject.name}`,
              subjectName: availableSubject.name,
              subjectCode: codeToUse,
              originalCode: groupSubject.code,
              databaseCode: availableSubject.subjectCode,
              groupName: group.groupName
            });
          }
        });

        return {
          hasSubgroups: false,
          groupCode: group.groupCode,
          description: group.description,
          subjects: groupSubjects
        };
      }
      return null;
    }
  }, []);

  // Categorize and filter subject options
  const getGroupedSubjectOptions = useCallback(() => {
    if (!selectedAdmissionClass || !classSubjects) {
      return {
        firstLanguages: [],
        vocational: [],
        other: [],
        groups: {},
        languageGroup: null,
        mainGroups: {},
        additionalGroup: null,
        nsqfGroup: null
      };
    }

    if (isEleventhTwelfthClass) {
      const mainGroupsMap: { [key: string]: any } = {};
      let additionalGroup: any = null;
      let nsqfGroup: any = null;

      const languageGroupDef = ELEVENTH_TWELFTH_CLASS_SUBJECTS_GROUPED.find(g => g.groupName === "LANGUAGE");
      const languageSubjectCodes = languageGroupDef?.subjects.map(s => s.code) || [];

      ELEVENTH_TWELFTH_CLASS_SUBJECTS_GROUPED.forEach((group) => {
        if (group.groupName === "LANGUAGE") return;

        const processedGroup = processGroup(group, classSubjects);
        if (!processedGroup) return;

        if (group.groupName === "LANGUAGE GROUP") {
          const filteredGroup = {
            ...processedGroup,
            subjects: processedGroup.subjects?.filter((subject: any) =>
              !languageSubjectCodes.includes(subject.subjectCode)
            ) || []
          };

          if (filteredGroup.hasSubgroups && filteredGroup.subgroups) {
            filteredGroup.subgroups = filteredGroup.subgroups.map((subgroup: any) => {
              if (subgroup.subgroupName === "A") return subgroup;
              return {
                ...subgroup,
                subjects: subgroup.subjects?.filter((subject: any) =>
                  !languageSubjectCodes.includes(subject.subjectCode)
                ) || []
              };
            });
          }

          if (filteredGroup.subjects.length > 0 ||
            (filteredGroup.subgroups && filteredGroup.subgroups.some((sg: any) => sg.subjects.length > 0))) {
            mainGroupsMap[group.groupName] = {
              groupName: group.groupName,
              ...filteredGroup
            };
          }
        } else if (group.groupName === "ADDITIONAL SUBJECT (OPTIONAL)") {
          const additionalSubjects = (processedGroup.subjects || []).map((subject: any) => ({
            ...subject,
            value: `ADDITIONAL SUBJECT (OPTIONAL)|${subject.subjectCode}`,
            groupName: group.groupName
          }));
          additionalGroup = {
            groupName: group.groupName,
            ...processedGroup,
            subjects: additionalSubjects
          };
        } else if (group.groupName === "NSQF SUBJECT (OPTIONAL)") {
          const nsqfSubjects = (processedGroup.subjects || []).map((subject: any) => ({
            ...subject,
            value: `NSQF SUBJECT (OPTIONAL)|${subject.subjectCode}`,
            groupName: group.groupName
          }));
          nsqfGroup = {
            groupName: group.groupName,
            ...processedGroup,
            subjects: nsqfSubjects
          };
        } else {
          mainGroupsMap[group.groupName] = {
            groupName: group.groupName,
            ...processedGroup
          };
        }
      });

      if (languageGroupDef) {
        const processedLangGroup = processGroup(languageGroupDef, classSubjects);
        if (processedLangGroup) {
          const subgroupASubjectCodes = ['051', '052', '053', '054', '055'];
          const langSubjects = (processedLangGroup.subjects || [])
            .filter((subject: any) => subgroupASubjectCodes.includes(subject.subjectCode))
            .map((subject: any) => ({
              ...subject,
              value: `LANGUAGE|${subject.subjectCode}`,
              groupName: "LANGUAGE"
            }));
          const langGroupData = {
            groupName: "LANGUAGE",
            groupCode: null,
            description: languageGroupDef.description,
            subjects: langSubjects
          };
          return {
            firstLanguages: [],
            vocational: [],
            other: [],
            groups: mainGroupsMap,
            languageGroup: langGroupData,
            mainGroups: mainGroupsMap,
            additionalGroup,
            nsqfGroup
          };
        }
      }

      return {
        firstLanguages: [],
        vocational: [],
        other: [],
        groups: mainGroupsMap,
        languageGroup: null,
        mainGroups: mainGroupsMap,
        additionalGroup,
        nsqfGroup
      };
    }

    const firstLanguages: any[] = [];
    const vocational: any[] = [];
    const other: any[] = [];

    classSubjects.forEach((subject: any) => {
      const normalizedSubjectName = subject.name?.toLowerCase().trim() || '';
      const isVocational = vocationalSubjects?.has(normalizedSubjectName) ?? false;
      const subjectValue = subject.subjectCode;

      if (isVocational) {
        vocational.push({ value: subjectValue, label: `(${subject.subjectCode}) ${subject.name}`, subjectName: subject.name, subjectCode: subject.subjectCode });
      } else {
        const isLang = ["051", "052", "053", "054", "055", "401", "411", "502", "504", "503", "505", "506", "507", "508", "509"].includes(subject.subjectCode) ||
          ['hindi', 'english', 'marathi', 'gujarati', 'sanskrit', 'urdu', 'bengali', 'telugu', 'tamil', 'kannada', 'malayalam', 'punjabi', 'odia'].some(keyword => subject.name.toLowerCase().includes(keyword));

        if (isLang) {
          firstLanguages.push({ value: subjectValue, label: `(${subject.subjectCode}) ${subject.name}`, subjectName: subject.name, subjectCode: subject.subjectCode });
        } else {
          other.push({ value: subjectValue, label: `(${subject.subjectCode}) ${subject.name}`, subjectName: subject.name, subjectCode: subject.subjectCode });
        }
      }
    });

    return {
      firstLanguages,
      vocational,
      other,
      groups: {},
      languageGroup: null,
      mainGroups: {},
      additionalGroup: null,
      nsqfGroup: null
    };
  }, [selectedAdmissionClass, classSubjects, vocationalSubjects, isEleventhTwelfthClass, processGroup, isLanguageSubject]);

  // Get selected language subjects
  const getSelectedLanguageSubjects = useCallback((): string[] => {
    return formData.subjects.filter(storedValue => {
      if (storedValue.startsWith('LANGUAGE|') && !storedValue.startsWith('LANGUAGE GROUP|')) return true;
      if (storedValue.includes('|')) return false;
      // Fallback for pure codes
      const subject = classSubjects?.find((cs: any) => cs.subjectCode === storedValue);
      return isLanguageSubject(storedValue, subject?.name);
    });
  }, [formData.subjects, classSubjects, isLanguageSubject]);

  // Clear stream if less than 2 languages are selected
  useEffect(() => {
    if (isEleventhTwelfthClass && open) {
      const selectedLanguages = getSelectedLanguageSubjects();
      if (selectedLanguages.length < 2 && selectedStream) {
        setSelectedStream('');
        setFormData(prev => ({ ...prev, stream: '' }));
        setSelectedMainGroup(null);
      }
    }
  }, [formData.subjects, isEleventhTwelfthClass, open, selectedStream, getSelectedLanguageSubjects]);

  // Function to sort subjects in order
  const getSortedSubjects = useCallback((): string[] => {
    if (!isEleventhTwelfthClass) return formData.subjects;

    const sortedSubjects: string[] = [];

    // 1. First two languages
    const initialLanguages = formData.subjects.filter(storedValue => {
      if (storedValue.startsWith('LANGUAGE|') && !storedValue.startsWith('LANGUAGE GROUP|')) return true;
      if (storedValue.includes('|')) return false;
      const subject = classSubjects?.find((cs: any) => cs.subjectCode === storedValue);
      return isLanguageSubject(storedValue, subject?.name);
    });
    sortedSubjects.push(...initialLanguages.slice(0, 2));

    // 2. Main group subjects
    const mainGroupSubjects = formData.subjects.filter(storedValue => {
      // Exclude initial languages
      if (initialLanguages.includes(storedValue)) return false;

      // Exclude optional subjects
      if (storedValue.startsWith('ADDITIONAL SUBJECT (OPTIONAL)|') || storedValue.startsWith('NSQF SUBJECT (OPTIONAL)|')) return false;

      // If it's another prefixed subject (like LANGUAGE GROUP or main group), or an unconverted subject
      return true;
    });

    const subjectsByGroup: { [key: string]: string[] } = {};
    mainGroupSubjects.forEach(storedValue => {
      const groupName = storedValue.includes('|') ? storedValue.split('|')[0] : 'OTHER';
      if (!subjectsByGroup[groupName]) subjectsByGroup[groupName] = [];
      subjectsByGroup[groupName].push(storedValue);
    });

    Object.keys(subjectsByGroup).sort().forEach(groupName => {
      sortedSubjects.push(...subjectsByGroup[groupName]);
    });

    // 3. Optional subjects
    const optionalSubjects = formData.subjects.filter(storedValue =>
      storedValue.startsWith('ADDITIONAL SUBJECT (OPTIONAL)|') ||
      storedValue.startsWith('NSQF SUBJECT (OPTIONAL)|')
    );
    sortedSubjects.push(...optionalSubjects);

    return sortedSubjects;
  }, [formData.subjects, isEleventhTwelfthClass, classSubjects, isLanguageSubject]);

  // Get selected main group subjects
  const getSelectedMainGroupSubjects = useCallback((): string[] => {
    if (!selectedMainGroup) return [];
    return formData.subjects.filter(storedValue => storedValue.startsWith(`${selectedMainGroup}|`));
  }, [formData.subjects, selectedMainGroup]);

  // Get all main group subjects
  const getAllMainGroupSubjects = useCallback((): string[] => {
    const mainGroups = ["HUMANITIES GROUP", "SCIENCE MATHS GROUP", "COMMERCE GROUP", "AGRICULTURE GROUP", "FINE ART GROUP", "HOME SCIENCE GROUP", "SCIENCE BIO. GROUP", "LANGUAGE GROUP"];
    return formData.subjects.filter(storedValue => mainGroups.some(group => storedValue.startsWith(`${group}|`)));
  }, [formData.subjects]);

  // Get names of subjects selected in any main group
  const getSelectedMainGroupSubjectNames = useCallback((): Set<string> => {
    const names = new Set<string>();
    if (!allSubjects) return names;

    const mainGroupSubjects = getAllMainGroupSubjects();
    mainGroupSubjects.forEach(storedValue => {
      const code = extractSubjectCode(storedValue);
      const subject = allSubjects.find((cs: any) => cs.subjectCode === code);
      if (subject?.name) names.add(subject.name);
    });
    return names;
  }, [allSubjects, getAllMainGroupSubjects, extractSubjectCode]);

  // Get selected optional subject
  const getSelectedOptionalSubject = useCallback((): string | null => {
    return formData.subjects.find(storedValue =>
      storedValue.startsWith('ADDITIONAL SUBJECT (OPTIONAL)|') ||
      storedValue.startsWith('NSQF SUBJECT (OPTIONAL)|')
    ) || null;
  }, [formData.subjects]);

  // Get all selected subject codes
  const getSelectedSubjectCodes = useCallback((): Set<string> => {
    const codes = new Set<string>();
    formData.subjects.forEach(storedValue => codes.add(extractSubjectCode(storedValue)));
    return codes;
  }, [formData.subjects, extractSubjectCode]);

  // Toggle language subject
  const handleToggleLanguageSubject = useCallback((subjectValue: string) => {
    if (isFieldReadOnly('subjects')) return;
    setFormData(prev => {
      const isSelected = prev.subjects.includes(subjectValue);
      const selectedLanguages = prev.subjects.filter(storedValue => storedValue.startsWith('LANGUAGE|'));

      if (isSelected) {
        const mainGroups = ["HUMANITIES GROUP", "SCIENCE MATHS GROUP", "COMMERCE GROUP", "AGRICULTURE GROUP", "FINE ART GROUP", "HOME SCIENCE GROUP", "SCIENCE BIO. GROUP", "LANGUAGE GROUP"];
        let finalSubjects = prev.subjects.filter(storedValue => {
          const isMainGroup = mainGroups.some(group => storedValue.startsWith(`${group}|`));
          const isOptional = storedValue.startsWith('ADDITIONAL SUBJECT (OPTIONAL)|') || storedValue.startsWith('NSQF SUBJECT (OPTIONAL)|');
          return !isMainGroup && !isOptional;
        });
        finalSubjects = finalSubjects.filter(code => code !== subjectValue);

        if (finalSubjects.length === 0) {
          setSelectedMainGroup(null);
          setSelectedStream('');
        }
        return { ...prev, subjects: finalSubjects, stream: '' };
      } else {
        if (selectedLanguages.length >= 2 || prev.subjects.length >= 6) return prev;
        return { ...prev, subjects: [...prev.subjects, subjectValue] };
      }
    });
  }, []);

  // Toggle main group
  const handleToggleMainGroup = useCallback((groupName: string) => {
    if (isFieldReadOnly('subjects')) return;
    if (selectedMainGroup === groupName) {
      setFormData(prev => {
        const newSubjects = prev.subjects.filter(storedValue => !storedValue.startsWith(`${groupName}|`));
        if (newSubjects.length === 0) setSelectedStream('');
        return { ...prev, subjects: newSubjects, stream: newSubjects.length === 0 ? '' : prev.stream };
      });
      setSelectedMainGroup(null);
      setExpandedGroups(prev => {
        const newSet = new Set(prev);
        newSet.delete(groupName);
        return newSet;
      });
    } else {
      if (selectedMainGroup) {
        setFormData(prev => ({
          ...prev,
          subjects: prev.subjects.filter(storedValue => !storedValue.startsWith(`${selectedMainGroup}|`))
        }));
        setExpandedGroups(prev => {
          const newSet = new Set(prev);
          newSet.delete(selectedMainGroup);
          return newSet;
        });
      }
      setSelectedMainGroup(groupName);
      setExpandedGroups(prev => {
        const newSet = new Set(prev);
        newSet.add(groupName);
        return newSet;
      });
    }
  }, [selectedMainGroup]);

  // Toggle main group subject
  const handleToggleMainGroupSubject = useCallback((subjectValue: string) => {
    if (isFieldReadOnly('subjects') || !selectedMainGroup) return;

    setFormData(prev => {
      const groupName = subjectValue.split('|')[0];
      const subjectCode = extractSubjectCode(subjectValue);
      const actualStoredValue = prev.subjects.find(storedValue => extractSubjectCode(storedValue) === subjectCode);
      const isSelected = !!actualStoredValue;
      const subjectsInThisGroup = prev.subjects.filter(storedValue => storedValue.startsWith(`${groupName}|`));

      if (groupName === "HUMANITIES GROUP") {
        const subgroupACodes = ["110", "120", "130", "140"];
        const subgroupBCodes = ["150", "151", "162", "163", "164", "165", "166", "167", "168"];
        const isSubgroupA = subgroupACodes.includes(subjectCode);
        const isSubgroupB = subgroupBCodes.includes(subjectCode);

        const subgroupACount = subjectsInThisGroup.filter(s => subgroupACodes.includes(extractSubjectCode(s))).length;

        if (isSelected && actualStoredValue) {
          let updatedSubjects = prev.subjects.filter(s => s !== actualStoredValue);
          if (isSubgroupA && (subgroupACount - 1) < 2) {
            updatedSubjects = updatedSubjects.filter(s => !(s.startsWith("HUMANITIES GROUP|") && subgroupBCodes.includes(extractSubjectCode(s))));
          }
          return { ...prev, subjects: updatedSubjects };
        } else {
          if (isSubgroupA && subgroupACount >= 3) return prev;
          if (isSubgroupB && (subgroupACount < 2 || subjectsInThisGroup.length >= 3)) return prev;
          if (prev.subjects.length >= 6) return prev;

          let updatedSubjects = [...prev.subjects, subjectValue];
          if (isSubgroupA && subgroupACount === 2) {
            updatedSubjects = updatedSubjects.filter(s => !(s.startsWith("HUMANITIES GROUP|") && subgroupBCodes.includes(extractSubjectCode(s))));
          }
          return { ...prev, subjects: updatedSubjects };
        }
      } else if (groupName === "COMMERCE GROUP") {
        const subgroupACodes = ["150", "151"];
        const subgroupBCodes = ["140"];
        const isSubgroupA = subgroupACodes.includes(subjectCode);
        const isSubgroupB = subgroupBCodes.includes(subjectCode);

        const subgroupACount = subjectsInThisGroup.filter(s => subgroupACodes.includes(extractSubjectCode(s))).length;

        if (isSelected && actualStoredValue) {
          let updatedSubjects = prev.subjects.filter(s => s !== actualStoredValue);
          if (isSubgroupA && (subgroupACount - 1) < 2) {
            updatedSubjects = updatedSubjects.filter(s => !(s.startsWith("COMMERCE GROUP|") && subgroupBCodes.includes(extractSubjectCode(s))));
          }
          return { ...prev, subjects: updatedSubjects };
        } else {
          if (isSubgroupA && subgroupACount >= 2) return prev;
          if (isSubgroupB && (subgroupACount < 2 || subjectsInThisGroup.length >= 3)) return prev;
          if (prev.subjects.length >= 6) return prev;
          return { ...prev, subjects: [...prev.subjects, subjectValue] };
        }
      } else if (groupName === "SCIENCE MATHS GROUP" || groupName === "SCIENCE BIO. GROUP" || groupName === "AGRICULTURE GROUP" || groupName === "FINE ART GROUP" || groupName === "HOME SCIENCE GROUP") {
        if (isSelected && actualStoredValue) return { ...prev, subjects: prev.subjects.filter(s => s !== actualStoredValue) };
        if (subjectsInThisGroup.length >= 3 || prev.subjects.length >= 6) return prev;
        return { ...prev, subjects: [...prev.subjects, subjectValue] };
      } else if (groupName === "LANGUAGE GROUP") {
        const subgroupACodes = ["051", "052", "053", "054", "055"];
        const subgroupBCodes = ["110", "120", "130", "140", "166"];
        const isSubgroupA = subgroupACodes.includes(subjectCode);
        const isSubgroupB = subgroupBCodes.includes(subjectCode);

        const subgroupACount = subjectsInThisGroup.filter(s => subgroupACodes.includes(extractSubjectCode(s))).length;
        const subgroupBCount = subjectsInThisGroup.filter(s => subgroupBCodes.includes(extractSubjectCode(s))).length;

        if (isSelected && actualStoredValue) {
          return { ...prev, subjects: prev.subjects.filter(s => s !== actualStoredValue) };
        } else {
          if (isSubgroupA && subgroupACount >= 1) return prev;
          if (isSubgroupB && subgroupBCount >= 2) return prev;
          if (prev.subjects.length >= 6) return prev;
          return { ...prev, subjects: [...prev.subjects, subjectValue] };
        }
      }
      return prev;
    });
  }, [selectedMainGroup, extractSubjectCode, isFieldReadOnly]);

  // Function to handle additional/NSQF subject selection (only one from either, max 6 total)
  const handleToggleOptionalSubject = useCallback((subjectValue: string) => {
    if (isFieldReadOnly('subjects')) return;

    setFormData(prev => {
      const isSelected = prev.subjects.includes(subjectValue);
      // Find any selected optional subject (Additional or NSQF)
      const selectedOptional = prev.subjects.find(storedValue =>
        storedValue.startsWith('ADDITIONAL SUBJECT (OPTIONAL)|') ||
        storedValue.startsWith('NSQF SUBJECT (OPTIONAL)|')
      );

      if (isSelected) {
        // Remove the subject
        return {
          ...prev,
          subjects: prev.subjects.filter(code => code !== subjectValue)
        };
      } else {
        // If another optional subject is selected, remove it first
        if (selectedOptional) {
          return {
            ...prev,
            subjects: prev.subjects.filter(code => code !== selectedOptional).concat(subjectValue)
          };
        }
        // Check if total subject limit (6) is reached
        if (prev.subjects.length >= 6) {
          return prev;
        }
        // Add the subject
        return {
          ...prev,
          subjects: [...prev.subjects, subjectValue]
        };
      }
    });
  }, [isFieldReadOnly]);

  // Function to toggle subject selection (add if not selected, remove if selected)
  // This is the main handler that routes to appropriate handler based on subject type
  const handleToggleSubject = useCallback((subjectValue: string) => {
    if (isFieldReadOnly('subjects')) return;

    // subjectValue is now in format "GROUP|CODE", extract the group name
    if (subjectValue.includes('|')) {
      const groupName = subjectValue.split('|')[0];

      if (groupName === 'LANGUAGE') {
        handleToggleLanguageSubject(subjectValue);
      } else if (groupName === 'ADDITIONAL SUBJECT (OPTIONAL)') {
        handleToggleOptionalSubject(subjectValue);
      } else if (groupName === 'NSQF SUBJECT (OPTIONAL)') {
        handleToggleOptionalSubject(subjectValue);
      } else {
        // Main group subject
        handleToggleMainGroupSubject(subjectValue);
      }
    } else {
      // Fallback for old format (shouldn't happen, but handle gracefully)
      const subjectCode = subjectValue;
      const subject = classSubjects?.find((cs: any) => cs.subjectCode === subjectCode);
      if (!subject) return;

      if (isLanguageSubject(subjectCode, subject.name)) {
        handleToggleLanguageSubject(`LANGUAGE|${subjectCode}`);
      } else if (isAdditionalSubject(subjectCode, subject.name)) {
        handleToggleOptionalSubject(`ADDITIONAL SUBJECT (OPTIONAL)|${subjectCode}`);
      } else if (isNSQFSubject(subjectCode, subject.name)) {
        handleToggleOptionalSubject(`NSQF SUBJECT (OPTIONAL)|${subjectCode}`);
      } else {
        // Try to find main group
        const mainGroupName = getMainGroupName(subjectCode, subject.name);
        if (mainGroupName) {
          handleToggleMainGroupSubject(`${mainGroupName}|${subjectCode}`);
        }
      }
    }
  }, [classSubjects, isLanguageSubject, isAdditionalSubject, isNSQFSubject, getMainGroupName, handleToggleLanguageSubject, handleToggleOptionalSubject, handleToggleMainGroupSubject, isFieldReadOnly]);

  // Updated handleRemoveSubject using getSortedSubjects
  const handleRemoveSubject = useCallback((sortedIndex: number) => {
    if (isFieldReadOnly('subjects')) return;
    setFormData(prev => {
      const sortedSubjects = (() => {
        if (!isEleventhTwelfthClass) return prev.subjects;
        const initialLanguages = prev.subjects.filter(s => s.startsWith('LANGUAGE|') && !s.startsWith('LANGUAGE GROUP|'));
        const mainGroups = ["HUMANITIES GROUP", "SCIENCE MATHS GROUP", "COMMERCE GROUP", "AGRICULTURE GROUP", "FINE ART GROUP", "HOME SCIENCE GROUP", "SCIENCE BIO. GROUP", "LANGUAGE GROUP"];
        const mainGroupSubjects = prev.subjects.filter(s => mainGroups.some(g => s.startsWith(`${g}|`)));
        const optionalSubjects = prev.subjects.filter(s => s.startsWith('ADDITIONAL SUBJECT (OPTIONAL)|') || s.startsWith('NSQF SUBJECT (OPTIONAL)|'));
        return [...initialLanguages, ...mainGroupSubjects, ...optionalSubjects];
      })();

      const subjectToRemove = sortedSubjects[sortedIndex];
      return { ...prev, subjects: prev.subjects.filter(s => s !== subjectToRemove) };
    });
  }, [isEleventhTwelfthClass, isFieldReadOnly]);

  const handleToggleGroup = useCallback((groupName: string) => {
    setExpandedGroups(prev => {
      const newSet = new Set(prev);
      if (newSet.has(groupName)) {
        newSet.delete(groupName);
      } else {
        newSet.add(groupName);
      }
      return newSet;
    });
  }, []);

  // Function to toggle subgroup expansion
  const handleToggleSubgroup = useCallback((groupName: string, subgroupName: string) => {
    const key = `${groupName}-${subgroupName}`;
    setExpandedSubgroups(prev => {
      const newSet = new Set(prev);
      if (newSet.has(key)) {
        newSet.delete(key);
      } else {
        newSet.add(key);
      }
      return newSet;
    });
  }, []);



  // Effect to handle subject format conversion and UI expansion for Class 11th/12th
  useEffect(() => {
    if (open && isEleventhTwelfthClass && formData.subjects.length > 0) {
      const languageSubjectCodes: string[] = [];
      const otherSubjects: string[] = [];

      // First pass: Separate subjects and handle already-converted ones
      formData.subjects.forEach((storedValue: string, index: number) => {
        const subjectCode = extractSubjectCode(storedValue);
        const subject = classSubjects?.find((cs: any) => cs.subjectCode === subjectCode);
        const isLang = isLanguageSubject(subjectCode, subject?.name);

        if (storedValue.startsWith('LANGUAGE|') && !storedValue.startsWith('LANGUAGE GROUP|')) {
          // Already a primary language
          languageSubjectCodes.push(subjectCode);
        } else if (isLang && index < 2 && !storedValue.includes('|')) {
          // Pure code that is a language in the first two slots
          languageSubjectCodes.push(subjectCode);
        } else {
          // Everything else
          otherSubjects.push(storedValue);
        }
      });

      const convertedSubjects: string[] = [];

      // Process primary languages (max 2)
      languageSubjectCodes.forEach((code, index) => {
        if (index < 2) {
          convertedSubjects.push(`LANGUAGE|${code}`);
        } else {
          // If somehow more than 2 languages ended up here, treat as other
          otherSubjects.push(code);
        }
      });

      // Process other subjects
      otherSubjects.forEach((storedValue) => {
        if (storedValue.includes('|')) {
          convertedSubjects.push(storedValue);
          return;
        }

        const subjectCode = storedValue;
        const subject = classSubjects?.find((cs: any) => cs.subjectCode === subjectCode);
        const isLang = isLanguageSubject(subjectCode, subject?.name);

        if (isLang) {
          convertedSubjects.push(`LANGUAGE GROUP|${subjectCode}`);
        } else if (isAdditionalSubject(subjectCode, subject?.name)) {
          convertedSubjects.push(`ADDITIONAL SUBJECT (OPTIONAL)|${subjectCode}`);
        } else if (isNSQFSubject(subjectCode, subject?.name)) {
          convertedSubjects.push(`NSQF SUBJECT (OPTIONAL)|${subjectCode}`);
        } else {
          const mainGroupName = getMainGroupName(subjectCode, subject?.name);
          if (mainGroupName) {
            convertedSubjects.push(`${mainGroupName}|${subjectCode}`);
          } else {
            convertedSubjects.push(storedValue);
          }
        }
      });

      // Update formData if conversion changed anything
      const subjectsChanged = JSON.stringify(formData.subjects) !== JSON.stringify(convertedSubjects);
      if (subjectsChanged) {
        setFormData(prev => ({ ...prev, subjects: convertedSubjects }));
      }

      // UI Expansion and Stream Selection logic
      if (isEdit || (initialData && !isEdit)) {
        const groupsToExpand = new Set<string>();
        const subgroupsToExpand = new Set<string>();
        let foundMainGroup: string | null = null;

        convertedSubjects.forEach(storedValue => {
          if (storedValue.includes('|')) {
            const [groupName, subjectCode] = storedValue.split('|');
            if (groupName === 'LANGUAGE') {
              setLanguageGroupExpanded(true);
            } else if (groupName === 'ADDITIONAL SUBJECT (OPTIONAL)' || groupName === 'NSQF SUBJECT (OPTIONAL)') {
              groupsToExpand.add(groupName);
            } else {
              const mainGroups = ["HUMANITIES GROUP", "SCIENCE MATHS GROUP", "COMMERCE GROUP", "AGRICULTURE GROUP", "FINE ART GROUP", "HOME SCIENCE GROUP", "SCIENCE BIO. GROUP", "LANGUAGE GROUP"];
              if (mainGroups.includes(groupName)) {
                groupsToExpand.add(groupName);
                if (!foundMainGroup) foundMainGroup = groupName;

                const groupDef = ELEVENTH_TWELFTH_CLASS_SUBJECTS_GROUPED.find(g => g.groupName === groupName);
                if (groupDef && groupDef.hasSubgroups && groupDef.subgroups) {
                  groupDef.subgroups.forEach(subgroup => {
                    if (subgroup.subjects.some(s => s.code === subjectCode)) {
                      subgroupsToExpand.add(`${groupName}-${subgroup.subgroupName}`);
                    }
                  });
                }
              }
            }
          }
        });

        if (foundMainGroup && !selectedMainGroup) {
          setSelectedMainGroup(foundMainGroup);
          const streamOption = streamOptions.find(opt => opt.groupName === foundMainGroup);
          if (streamOption && !selectedStream) setSelectedStream(streamOption.value);
        }

        // Use a functional update to avoid missing previous expansions
        setExpandedGroups(prev => new Set([...Array.from(prev), ...Array.from(groupsToExpand)]));
        setExpandedSubgroups(prev => new Set([...Array.from(prev), ...Array.from(subgroupsToExpand)]));
      }
    } else if (open && !isEdit && !initialData) {
      setSelectedMainGroup(null);
      setSelectedStream('');
      setExpandedGroups(new Set());
      setExpandedSubgroups(new Set());
    }
  }, [open, isEleventhTwelfthClass, formData.subjects, classSubjects, isEdit, initialData, isLanguageSubject, isAdditionalSubject, isNSQFSubject, getMainGroupName, streamOptions, selectedMainGroup, selectedStream]);

  const handleChange = useCallback((e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;

    // Handle subject selection
    if (name === 'addSubject') {
      handleAddSubject(value);
      return;
    }

    // Prevent changes to mobile number if it's read-only (user.email is available)
    if (name === 'mobileNo' && user?.email) {
      return;
    }

    // Clear validation error for this field when user types
    if (validationErrors[name]) {
      setValidationErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[name];
        return newErrors;
      });
    }

    setFormData(prev => ({ ...prev, [name]: value }));
  }, [handleAddSubject, validationErrors, user?.email]);

  // Handle date changes for DatePicker components
  const handleDateChange = useCallback((name: string, value: Date | null) => {
    try {
      // Clear validation error for this field when user selects date
      if (validationErrors[name]) {
        setValidationErrors(prev => {
          const newErrors = { ...prev };
          delete newErrors[name];
          return newErrors;
        });
      }

      if (value && isValid(value)) {
        const formattedDate = format(value, 'dd/MM/yyyy');
        setFormData(prev => ({ ...prev, [name]: formattedDate }));
      } else {
        // Set empty string if no valid date is provided (no default)
        setFormData(prev => ({ ...prev, [name]: '' }));
      }
    } catch (error) {
      // If formatting fails, set empty string (no default)
      setFormData(prev => ({ ...prev, [name]: '' }));
    }
  }, [validationErrors]);

  // Helper function to ensure dates are always valid in dd/MM/yyyy format (as expected by backend)
  const ensureValidDates = useCallback((data: AdmissionFromData) => {
    // Ensure dateOfBirth is valid
    let dateOfBirth = data.dateOfBirth;
    if (dateOfBirth) {
      try {
        const parsedDate = new Date(dateOfBirth);
        if (isNaN(parsedDate.getTime())) {
          dateOfBirth = ''; // Set to empty if invalid
        } else {
          dateOfBirth = format(parsedDate, 'dd/MM/yyyy');
        }
      } catch (error) {
        dateOfBirth = ''; // Set to empty if parsing fails
      }
    }

    // Ensure admissionDate is valid
    let admissionDate = data.admissionDate;
    if (admissionDate) {
      try {
        const parsedDate = new Date(admissionDate);
        if (isNaN(parsedDate.getTime())) {
          admissionDate = ''; // Set to empty if invalid
        } else {
          admissionDate = format(parsedDate, 'dd/MM/yyyy');
        }
      } catch (error) {
        admissionDate = ''; // Set to empty if parsing fails
      }
    }

    return {
      ...data,
      dateOfBirth,
      admissionDate,
    };
  }, []);

  const SectionTitle = ({ title }: { title: string }) => (
    <Box sx={{
      p: 1,
      mb: 2,
      backgroundColor: '#f8f9fb',
      borderLeft: '4px solid #0168EA',
      borderRadius: 1,
      boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
    }}>
      <Typography variant="h6" sx={{
        color: '#0168EA',
        fontWeight: 600,
        mb: 0
      }}>
        {t(title)}
      </Typography>
    </Box>
  );

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <Dialog
        open={open}
        onClose={() => { }} // Prevent closing on backdrop click
        maxWidth="lg"
        fullWidth
        disableEscapeKeyDown
        key={isEdit && initialData?.id ? `edit-${initialData.id}` : 'new-form'}
      >
        <DialogTitle sx={{
          backgroundColor: '#f8f9fb',
          borderBottom: '2px solid #e0e7ff',
          color: '#0168EA',
          fontWeight: 600,
          fontSize: '1.5rem',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center'
        }}>
          <span style={{
            color: (isEdit && (formData.isRegistered === 'Rejected' || initialData?.isRegistered === 'Rejected'))
              ? '#d32f2f'
              : '#0168EA'
          }}>
            {isEdit && (formData.isRegistered === 'Rejected' || initialData?.isRegistered === 'Rejected')
              ? t('your_application_form_is_rejected') || 'Your application form is rejected'
              : isEdit
                ? t('edit_application_form')
                : t('application_form_for_user')}
          </span>

        </DialogTitle>
        <DialogContent>
          <Paper sx={{ p: 2 }}>
            {/* School Header Information */}
            <Box sx={{
              mb: 1,
              p: 1.5,
              background: '#3092e3',
              borderRadius: 2,
              color: 'white',
              boxShadow: '0 4px 12px rgba(1, 104, 234, 0.3)'
            }}>
              <Typography variant="h4" fontWeight="bold" sx={{ mb: 2, textAlign: 'center', color: 'white' }}>
                {t("application_form_a")}
              </Typography>

              <Grid container spacing={2} sx={{ ml: 2 }}>
                <Grid item xs={12} md={6}>
                  <Typography variant="body1" sx={{ color: 'rgba(255,255,255,0.9)' }}>
                    <strong>{t("name_of_school")}:</strong> {schoolData?.nameofSchool || t("school_name_not_available")}
                  </Typography>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography variant="body1" sx={{ color: 'rgba(255,255,255,0.9)' }}>
                    <strong>{t("school_code")}:</strong> {schoolData?.schoolCode || t("code_not_available")}
                  </Typography>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography variant="body1" sx={{ color: 'rgba(255,255,255,0.9)' }}>
                    <strong>{t("district")}:</strong> {schoolData?.district || t("district_not_available")}
                  </Typography>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography variant="body1" sx={{ color: 'rgba(255,255,255,0.9)' }}>
                    <strong>{t("dise_code")}:</strong> {schoolData?.diseCode || t("dise_code_not_available")}
                  </Typography>
                </Grid>
              </Grid>
            </Box>

            <Divider sx={{ my: 4, borderColor: '#e0e7ff', borderWidth: 1 }} />

            {/* Section 1: Application & Enrollment Details */}
            <SectionTitle title="application_enrollment_details" />
            <Grid container spacing={1.7}>
              <Grid item xs={12} md={3}>
                <TextField
                  fullWidth
                  label={t('application_no')}
                  name="applicationNo"
                  value={formData.applicationNo || ''}
                  onChange={handleChange}
                  size="small"
                  margin="dense"
                  sx={{ backgroundColor: '#f8f9fb' }}
                />
              </Grid>
              <Grid item xs={12} md={3}>
                <TextField
                  fullWidth
                  label={t('roll_no')}
                  name="rollNo"
                  value={formData.rollNo || ''}
                  onChange={handleChange}
                  size="small"
                  margin="dense"
                  sx={{ backgroundColor: '#f8f9fb' }}
                />
              </Grid>
              <Grid item xs={12} md={3}>
                <TextField
                  fullWidth
                  label={t('enrolment_no')}
                  name="enrolmentNo"
                  value={formData.enrolmentNo || ''}
                  onChange={handleChange}
                  size="small"
                  margin="dense"
                  sx={{ backgroundColor: '#f8f9fb' }}
                />
              </Grid>
              <Grid item xs={12} md={3}>
                <TextField
                  fullWidth
                  label={t('scholar_no')}
                  name="scholarNo"
                  value={formData.scholarNo || ''}
                  onChange={handleChange}
                  size="small"
                  margin="dense"
                  sx={{ backgroundColor: '#f8f9fb' }}
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  label={t('sssmid')}
                  name="sssmid"
                  value={formData.sssmid || ''}
                  onChange={handleChange}
                  size="small"
                  margin="dense"
                  sx={{ backgroundColor: '#f8f9fb' }}
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  label={t('pen_no')}
                  name="penNo"
                  value={formData.penNo || ''}
                  onChange={handleChange}
                  size="small"
                  margin="dense"
                  sx={{ backgroundColor: '#f8f9fb' }}
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  label={t("appar_id_no")}
                  name="apparId"
                  value={formData.apparId || ''}
                  onChange={handleChange}
                  size="small"
                  margin="dense"
                  sx={{ backgroundColor: '#f8f9fb' }}
                />
              </Grid>
            </Grid>

            <Divider sx={{ my: 4, borderColor: '#e0e7ff', borderWidth: 1 }} />

            {/* Section 2: Student Personal Information */}
            <SectionTitle title="personal_information" />
            <Grid container spacing={1.7}>
              <Grid item xs={12} md={3}>
                <TextField
                  fullWidth
                  label={
                    <span>
                      {t('student_name')} <span style={{ color: 'red' }}>*</span>
                    </span>
                  }
                  name="studentName"
                  value={formData.studentName || ''}
                  onChange={handleChange}
                  error={!!validationErrors.studentName}
                  helperText={validationErrors.studentName || ''}
                  size="small"
                  margin="dense"
                  sx={{ backgroundColor: '#f8f9fb' }}
                />
              </Grid>
              <Grid item xs={12} md={3}>
                <TextField
                  fullWidth
                  label={
                    <span>
                      {t('father_name')} <span style={{ color: 'red' }}>*</span>
                    </span>
                  }
                  name="fatherName"
                  value={formData.fatherName || ''}
                  onChange={handleChange}
                  error={!!validationErrors.fatherName}
                  helperText={validationErrors.fatherName || ''}
                  size="small"
                  margin="dense"
                  sx={{ backgroundColor: '#f8f9fb' }}
                />
              </Grid>
              <Grid item xs={12} md={3}>
                <TextField
                  fullWidth
                  label={
                    <span>
                      {t('mother_name')} <span style={{ color: 'red' }}>*</span>
                    </span>
                  }
                  name="motherName"
                  value={formData.motherName || ''}
                  onChange={handleChange}
                  error={!!validationErrors.motherName}
                  helperText={validationErrors.motherName || ''}
                  size="small"
                  margin="dense"
                  sx={{ backgroundColor: '#f8f9fb' }}
                />
              </Grid>
              <Grid item xs={12} md={3}>
                <TextField
                  fullWidth
                  select
                  label={t('caste_certificate')}
                  name="casteCertificate"
                  value={formData.casteCertificate || ''}
                  onChange={handleChange}
                  size="small"
                  margin="dense"
                  sx={{ backgroundColor: '#f8f9fb' }}
                >
                  <MenuItem value="Yes">{t('yes')}</MenuItem>
                  <MenuItem value="No">{t('no')}</MenuItem>
                </TextField>
              </Grid>
              <Grid item xs={12} md={3}>
                <TextField
                  fullWidth
                  select
                  label={
                    <span>
                      {t('gender')} <span style={{ color: 'red' }}>*</span>
                    </span>
                  }
                  name="gender"
                  value={formData.gender || ''}
                  onChange={(e) => {
                    // Clear validation error for this field when user selects
                    if (validationErrors.gender) {
                      setValidationErrors(prev => {
                        const newErrors = { ...prev };
                        delete newErrors.gender;
                        return newErrors;
                      });
                    }
                    handleChange(e);
                  }}
                  error={!!validationErrors.gender}
                  helperText={validationErrors.gender || ''}
                  size="small"
                  margin="dense"
                  sx={{ backgroundColor: '#f8f9fb' }}
                >
                  <MenuItem value="M">{t('male')}</MenuItem>
                  <MenuItem value="F">{t('female')}</MenuItem>
                  <MenuItem value="O">{t('other')}</MenuItem>
                </TextField>
              </Grid>

              <Grid item xs={12} md={3}>
                <TextField
                  fullWidth
                  label={t('religion')}
                  name="religion"
                  value={formData.religion || ''}
                  onChange={handleChange}
                  size="small"
                  margin="dense"
                  sx={{ backgroundColor: '#f8f9fb' }}
                />
              </Grid>
              <Grid item xs={12} md={3}>
                <TextField
                  fullWidth
                  select
                  label={t('caste')}
                  name="caste"
                  value={formData.caste || ''}
                  onChange={handleChange}
                  size="small"
                  margin="dense"
                  sx={{ backgroundColor: '#f8f9fb' }}
                >
                  {CasteOptions.map((option) => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>
              <Grid item xs={12} md={3}>
                <TextField
                  fullWidth
                  label={t('sub_caste')}
                  name="subCaste"
                  value={formData.subCaste || ''}
                  onChange={handleChange}
                  size="small"
                  margin="dense"
                  sx={{ backgroundColor: '#f8f9fb' }}
                />
              </Grid>
              <Grid item xs={12} md={3}>
                <TextField
                  fullWidth
                  label={t('aadhar_no')}
                  name="aadharNo"
                  value={formData.aadharNo || ''}
                  onChange={handleChange}
                  size="small"
                  margin="dense"
                  sx={{ backgroundColor: '#f8f9fb' }}
                />
              </Grid>
              <Grid item xs={12} md={3}>
                <DatePicker
                  label={
                    <span>
                      {t('date_of_birth')} <span style={{ color: 'red' }}>*</span>
                    </span>
                  }
                  value={safeParseDateString(formData.dateOfBirth)}
                  onChange={(value) => handleDateChange('dateOfBirth', value)}
                  slotProps={{
                    textField: {
                      fullWidth: true,
                      error: !!validationErrors.dateOfBirth,
                      helperText: validationErrors.dateOfBirth || '',
                      size: 'small',
                      margin: 'dense',
                      sx: { backgroundColor: '#f8f9fb' }
                    }
                  }}
                  format="dd/MM/yyyy"
                />
              </Grid>
            </Grid>


            <Divider sx={{ my: 4, borderColor: '#e0e7ff', borderWidth: 1 }} />

            {/* Section 3: Contact & Address Information */}
            <SectionTitle title="contact_address" />
            <Grid container spacing={1.7}>
              <Grid item xs={12} md={3}>
                <TextField
                  fullWidth
                  label={t("house_no_building_apartment")}
                  name="houseNo"
                  value={formData.houseNo || ''}
                  onChange={handleChange}
                  size="small"
                  margin="dense"
                  sx={{ backgroundColor: '#f8f9fb' }}
                />
              </Grid>
              <Grid item xs={12} md={3}>
                <TextField
                  fullWidth
                  label={t("street_locality_sector")}
                  name="street"
                  value={formData.street || ''}
                  onChange={handleChange}
                  size="small"
                  margin="dense"
                  sx={{ backgroundColor: '#f8f9fb' }}
                />
              </Grid>
              <Grid item xs={12} md={3}>
                <TextField
                  fullWidth
                  label={t('landmark')}
                  name="landmark"
                  value={formData.landmark || ''}
                  onChange={handleChange}
                  size="small"
                  margin="dense"
                  sx={{ backgroundColor: '#f8f9fb' }}
                />
              </Grid>
              <Grid item xs={12} md={3}>
                <TextField
                  fullWidth
                  label={t("area_locality_sector")}
                  name="area"
                  value={formData.area || ''}
                  onChange={handleChange}
                  size="small"
                  margin="dense"
                  sx={{ backgroundColor: '#f8f9fb' }}
                />
              </Grid>
              <Grid item xs={12} md={3}>
                <TextField
                  fullWidth
                  label={t("village_town_city")}
                  name="city"
                  value={formData.city || ''}
                  onChange={handleChange}
                  size="small"
                  margin="dense"
                  sx={{ backgroundColor: '#f8f9fb' }}
                />
              </Grid>
              <Grid item xs={12} md={3}>
                <TextField
                  fullWidth
                  label={t('post_office')}
                  name="postOffice"
                  value={formData.postOffice || ''}
                  onChange={handleChange}
                  size="small"
                  margin="dense"
                  sx={{ backgroundColor: '#f8f9fb' }}
                />
              </Grid>
              <Grid item xs={12} md={3}>
                <TextField
                  fullWidth
                  label={t('district')}
                  name="district"
                  value={formData.district || ''}
                  onChange={handleChange}
                  size="small"
                  margin="dense"
                  sx={{ backgroundColor: '#f8f9fb' }}
                />
              </Grid>
              <Grid item xs={12} md={3}>
                <TextField
                  fullWidth
                  label={t('state')}
                  name="state"
                  value={formData.state || ''}
                  onChange={handleChange}
                  size="small"
                  margin="dense"
                  sx={{ backgroundColor: '#f8f9fb' }}
                />
              </Grid>
              <Grid item xs={12} md={3}>
                <TextField
                  fullWidth
                  label={t('pin_code')}
                  name="pinCode"
                  value={formData.pinCode || ''}
                  onChange={handleChange}
                  size="small"
                  margin="dense"
                  sx={{ backgroundColor: '#f8f9fb' }}
                />
              </Grid>
              <Grid item xs={12} md={3}>
                <TextField
                  fullWidth
                  label={
                    <span>
                      {t('mobile_no')} <span style={{ color: 'red' }}>*</span>
                    </span>
                  }
                  name="mobileNo"
                  value={formData.mobileNo || user?.email || ''}
                  onChange={handleChange}
                  error={!!validationErrors.mobileNo}
                  helperText={validationErrors.mobileNo || ''}
                  size="small"
                  margin="dense"
                  disabled={!!user?.email || (isEdit && !isSchoolAdmin() && !isTeacher())}
                  InputProps={{
                    readOnly: !!user?.email,
                  }}
                  sx={{
                    backgroundColor: '#f8f9fb',
                    '& .MuiInputBase-input[readonly]': {
                      cursor: 'default',
                      backgroundColor: '#f5f5f5',
                    }
                  }}
                />
              </Grid>
              <Grid item xs={12} md={3}>
                <TextField
                  fullWidth
                  label={t('gmail')}
                  name="gmail"
                  type="email"
                  value={formData.gmail || ''}
                  onChange={handleChange}
                  size="small"
                  margin="dense"
                  sx={{ backgroundColor: '#f8f9fb' }}
                />
              </Grid>
            </Grid>

            <Divider sx={{ my: 4, borderColor: '#e0e7ff', borderWidth: 1 }} />

            {/* Section 4: Bank & Financial Information */}
            <SectionTitle title="bank_financial" />
            <Grid container spacing={1.7}>
              <Grid item xs={12} md={3}>
                <TextField
                  fullWidth
                  label={t('bank_name')}
                  name="bankName"
                  value={formData.bankName || ''}
                  onChange={handleChange}
                  size="small"
                  margin="dense"
                  sx={{ backgroundColor: '#f8f9fb' }}
                />
              </Grid>
              <Grid item xs={12} md={3}>
                <TextField
                  fullWidth
                  label={t('ifsc_code')}
                  name="ifcCode"
                  value={formData.ifcCode || ''}
                  onChange={handleChange}
                  size="small"
                  margin="dense"
                  sx={{ backgroundColor: '#f8f9fb' }}
                />
              </Grid>
              <Grid item xs={12} md={3}>
                <TextField
                  fullWidth
                  label={t('account_no')}
                  name="accountNo"
                  value={formData.accountNo || ''}
                  onChange={handleChange}
                  size="small"
                  margin="dense"
                  sx={{ backgroundColor: '#f8f9fb' }}
                />
              </Grid>
              <Grid item xs={12} md={3}>
                <TextField
                  fullWidth
                  label={t('annual_income')}
                  name="annualIncome"
                  value={formData.annualIncome || ''}
                  onChange={handleChange}
                  size="small"
                  margin="dense"
                  sx={{ backgroundColor: '#f8f9fb' }}
                />
              </Grid>
            </Grid>

            <Divider sx={{ my: 4, borderColor: '#e0e7ff', borderWidth: 1 }} />

            {/* Section 5: Admission Details */}
            <SectionTitle title="admission_details" />
            <Grid container spacing={2.5}>
              <Grid item xs={12} md={3}>
                <DatePicker
                  label={t('admission_date')}
                  value={safeParseDateString(formData.admissionDate)}
                  onChange={(value) => handleDateChange('admissionDate', value)}
                  slotProps={{
                    textField: {
                      fullWidth: true,
                      size: 'small',
                      margin: 'dense',
                      sx: { backgroundColor: '#f8f9fb' }
                    }
                  }}
                  format="dd/MM/yyyy"
                />
              </Grid>
              <Grid item xs={12} md={3}>
                <TextField
                  fullWidth
                  select
                  label={
                    <span>
                      {t('session')} <span style={{ color: 'red' }}>*</span>
                    </span>
                  }
                  name="sessionName"
                  value={formData.sessionName || ''}
                  onChange={(e) => {
                    const sessionName = e.target.value;

                    // Clear validation error for this field when user selects
                    if (validationErrors.sessionName) {
                      setValidationErrors(prev => {
                        const newErrors = { ...prev };
                        delete newErrors.sessionName;
                        return newErrors;
                      });
                    }

                    const selectedSession = availableSessions?.find(ses => ses.name === sessionName);
                    const newSessionId = selectedSession?.id || 0;

                    // Clear class selection when session changes (classes are session-specific)
                    setFormData(prev => ({
                      ...prev,
                      sessionName: sessionName,
                      sessionId: newSessionId,
                      admissionClass: '', // Clear admission class
                      section: '', // Clear section
                      classId: undefined, // Clear class ID
                      subjects: [], // Clear subjects when session changes
                    }));
                    setSelectedClassId(undefined);
                    setSelectedAdmissionClass('');
                    setSelectedSessionId(newSessionId || undefined);

                    // Clear class validation error when session changes
                    if (validationErrors.admissionClass) {
                      setValidationErrors(prev => {
                        const newErrors = { ...prev };
                        delete newErrors.admissionClass;
                        return newErrors;
                      });
                    }
                  }}
                  error={!!validationErrors.sessionName}
                  helperText={validationErrors.sessionName || ''}
                  size="small"
                  margin="dense"
                  sx={{ backgroundColor: '#f8f9fb' }}
                  disabled={!availableSessions || availableSessions.length === 0}
                >
                  {availableSessions?.map((session) => (
                    <MenuItem key={session.id} value={session.name}>
                      {session.name}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>
              <Grid item xs={12} md={3}>
                <TextField
                  fullWidth
                  select
                  SelectProps={{
                    MenuProps: {
                      PaperProps: {
                        style: { maxHeight: 250 }
                      },
                      anchorOrigin: {
                        vertical: 'bottom',
                        horizontal: 'left',
                      },
                      transformOrigin: {
                        vertical: 'top',
                        horizontal: 'left',
                      }
                    }
                  }}
                  label={
                    <span>
                      {t('admission_class')} <span style={{ color: 'red' }}>*</span>
                    </span>
                  }
                  name="admissionClass"
                  value={selectedClassId || ''}
                  onChange={(e) => {
                    // Validate that session is selected before allowing class selection
                    const currentSessionId = selectedSessionId || formData.sessionId;
                    if (!currentSessionId || currentSessionId === 0) {
                      setValidationErrors(prev => ({
                        ...prev,
                        admissionClass: t('please_select_session_first') || 'Please select a session first'
                      }));
                      return;
                    }

                    const classId = Number(e.target.value);

                    // Clear validation error for this field when user selects
                    if (validationErrors.admissionClass) {
                      setValidationErrors(prev => {
                        const newErrors = { ...prev };
                        delete newErrors.admissionClass;
                        return newErrors;
                      });
                    }

                    // Find the selected class by ID from filtered classes
                    const selectedClassData = filteredClasses?.find(cls => cls.id === classId);

                    if (selectedClassData) {
                      // Clear subjects when class changes (subjects are class-specific)
                      setFormData(prev => ({
                        ...prev,
                        admissionClass: selectedClassData.name,
                        section: selectedClassData.section || '',
                        classId: selectedClassData.id,
                        subjects: [], // Clear subjects when class changes
                      }));
                      setSelectedAdmissionClass(selectedClassData.name);
                      setSelectedClassId(selectedClassData.id);
                    }
                  }}
                  error={!!validationErrors.admissionClass || ((!formData.sessionId || formData.sessionId === 0) && !filteredClasses?.length)}
                  helperText={
                    validationErrors.admissionClass ||
                    ((!formData.sessionId || formData.sessionId === 0) && !filteredClasses?.length
                      ? (t('please_select_session_first') || 'Please select a session first')
                      : '')
                  }
                  size="small"
                  margin="dense"
                  sx={{
                    backgroundColor: '#f8f9fb',
                    '& .MuiOutlinedInput-root': {
                      '&:hover fieldset': {
                        borderColor: '#3092e3',
                      },
                      '&.Mui-focused fieldset': {
                        borderColor: '#3092e3',
                      },
                    },
                    '& .MuiInputLabel-root.Mui-focused': {
                      color: '#3092e3',
                    },
                  }}
                  disabled={!filteredClasses || filteredClasses.length === 0}
                >
                  {filteredClasses?.slice().sort((a, b) => {
                    const numA = parseInt(a.name);
                    const numB = parseInt(b.name);
                    return numA - numB;
                    })
                    .map(cls => (
                      <MenuItem key={cls.id} value={cls.id}>
                        {cls.name} {cls.section ? `- ${cls.section}` : ''}
                      </MenuItem>
                    ))
                  }
                </TextField>
              </Grid>
              <Grid item xs={12} md={3}>
                <TextField
                  fullWidth
                  label={t('section')}
                  name="section"
                  value={formData.section || ''}
                  placeholder=""
                  size="small"
                  margin="dense"
                  disabled
                  sx={{
                    backgroundColor: '#f8f9fb',
                    '& .MuiInputBase-input': {
                      color: '#666',
                      fontWeight: 500
                    },
                    '& .MuiOutlinedInput-root': {
                      '& fieldset': {
                        borderColor: '#e0e0e0',
                      },
                    },
                  }}
                />
              </Grid>
              <Grid item xs={12} md={3}>
                <TextField
                  fullWidth
                  select
                  label={
                    <span>
                      {t('medium')} <span style={{ color: 'red' }}>*</span>
                    </span>
                  }
                  name="medium"
                  value={formData.medium || ''}
                  onChange={(e) => {
                    // Clear validation error for this field when user selects
                    if (validationErrors.medium) {
                      setValidationErrors(prev => {
                        const newErrors = { ...prev };
                        delete newErrors.medium;
                        return newErrors;
                      });
                    }
                    handleChange(e);
                  }}
                  size="small"
                  margin="dense"
                  sx={{ backgroundColor: '#f8f9fb' }}
                  error={!!validationErrors.medium}
                  helperText={
                    validationErrors.medium ||
                    (() => {
                      if (!schoolData?.medium) return t("please_select_medium");

                      if (Array.isArray(schoolData.medium)) {
                        return t("please_select_from_available_mediums");
                      } else {
                        return t("please_select_from_available_mediums");
                      }
                    })()
                  }
                  disabled={false}
                >
                  {getAvailableMediumOptions().map((option) => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>
              <Grid item xs={12} md={3}>
                <TextField
                  fullWidth
                  select
                  label={t('Admission Type')}
                  name="admissionType"
                  value={formData.admissionType || ''}
                  onChange={handleChange}
                  size="small"
                  margin="dense"
                  sx={{ backgroundColor: '#f8f9fb' }}
                >
                  <MenuItem value="REGULAR">{t('REGULAR')}</MenuItem>
                  <MenuItem value="PRIVATE">{t('PRIVATE')}</MenuItem>
                </TextField>
              </Grid>
              <Grid item xs={12} md={3}>
                <TextField
                  fullWidth
                  label={t('cwsn_code')}
                  name="cwsnCode"
                  value={formData.cwsnCode || ''}
                  onChange={handleChange}
                  size="small"
                  margin="dense"
                  sx={{ backgroundColor: '#f8f9fb' }}
                />
              </Grid>
              <Grid item xs={12} md={3}>
                <TextField
                  fullWidth
                  label={t('sambal_kramank')}
                  name="sambalKramank"
                  value={formData.sambalKramank || ''}
                  onChange={handleChange}
                  size="small"
                  margin="dense"
                  sx={{ backgroundColor: '#f8f9fb' }}
                />
              </Grid>
            </Grid>

            <Divider sx={{ my: 4, borderColor: '#e0e7ff', borderWidth: 1 }} />

            {/* Section 6: Subjects / Academic Information */}
            <SectionTitle title="subjects_academic" />
            <Grid container spacing={2}>
              {/* For Class 11th/12th: New Subject Selection UI with Rules */}
              {isEleventhTwelfthClass ? (
                <Grid item xs={12}>
                  {subjectsLoading ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
                      <CircularProgress />
                    </Box>
                  ) : !classSubjects || classSubjects.length === 0 ? (
                    <Alert severity="info" sx={{ mt: 2 }}>
                      {t("no_subjects_available_for_class")}
                    </Alert>
                  ) : (() => {
                    const grouped = getGroupedSubjectOptions();
                    const selectedLanguages = getSelectedLanguageSubjects();
                    const selectedMainGroupSubjects = getSelectedMainGroupSubjects();
                    const allMainGroupSubjects = getAllMainGroupSubjects();
                    const selectedOptional = getSelectedOptionalSubject();
                    // Show optional groups if ANY main group has 3 subjects selected
                    const showOptionalGroups = allMainGroupSubjects.length >= 3;

                    // Helper function to render subjects
                    const renderSubjects = (subjects: any[], maxSelection: number, currentCount: number, onToggle: (value: string) => void, checkTotalLimit: boolean = false, groupName?: string, isSubgroupDisabled: boolean = false): React.ReactElement => {
                      const totalSubjectsCount = formData.subjects.length;
                      const isTotalLimitReached = checkTotalLimit && totalSubjectsCount >= 6;
                      return (
                        <Grid container spacing={1} sx={{ mt: 1 }}>
                          {subjects.map((subject: any) => {
                            const subjectCode = extractSubjectCode(subject.value);
                            const expectedGroupPrefix = subject.value.includes('|') ? subject.value.split('|')[0] : null;

                            const isSelected = formData.subjects.some((storedValue: string) => {
                              const storedCode = extractSubjectCode(storedValue);
                              if (groupName === 'ADDITIONAL SUBJECT (OPTIONAL)' || groupName === 'NSQF SUBJECT (OPTIONAL)') {
                                return storedCode === subjectCode &&
                                  storedValue.startsWith(expectedGroupPrefix || '');
                              }
                              if (expectedGroupPrefix && storedValue.startsWith(expectedGroupPrefix)) {
                                return storedCode === subjectCode;
                              }
                              return storedCode === subjectCode;
                            });
                            const isOptionalGroup = groupName === 'ADDITIONAL SUBJECT (OPTIONAL)' || groupName === 'NSQF SUBJECT (OPTIONAL)';
                            const hasReachedGroupLimit = !isOptionalGroup && currentCount >= maxSelection;
                            const isDisabled = isSubgroupDisabled || isFieldReadOnly('subjects') || (!isSelected && (hasReachedGroupLimit || isTotalLimitReached));
                            return (
                              <Grid item xs={12} sm={6} md={4} key={subject.value}>
                                <Box
                                  onClick={() => {
                                    if (!isDisabled) {
                                      onToggle(subject.value);
                                    }
                                  }}
                                  sx={{
                                    p: 1.5,
                                    borderRadius: 1,
                                    border: '1px solid',
                                    borderColor: isSelected ? '#4caf50' : '#e0e0e0',
                                    backgroundColor: isSelected ? '#e8f5e9' : 'transparent',
                                    cursor: isDisabled ? 'not-allowed' : 'pointer',
                                    display: 'flex',
                                    justifyContent: 'space-between',
                                    alignItems: 'center',
                                    transition: 'all 0.2s ease-in-out',
                                    opacity: isDisabled ? 0.5 : 1,
                                    '&:hover': {
                                      backgroundColor: isDisabled ? 'transparent' : (isSelected ? '#c8e6c9' : '#f5f5f5'),
                                      borderColor: isDisabled ? '#e0e0e0' : (isSelected ? '#4caf50' : '#bdbdbd'),
                                    },
                                  }}
                                >
                                  <Typography variant="body2" sx={{ fontWeight: isSelected ? 600 : 400 }}>
                                    {subject.label}
                                  </Typography>
                                  {isSelected && (
                                    <CheckCircleIcon sx={{ color: '#4caf50', fontSize: 20 }} />
                                  )}
                                </Box>
                              </Grid>
                            );
                          })}
                        </Grid>
                      );
                    };

                    return (
                      <Box sx={{ mt: 2 }}>
                        {/* Step 1: Language Group - Always visible, expanded by default */}
                        {grouped.languageGroup && (
                          <Paper
                            elevation={1}
                            sx={{
                              mb: 2,
                              border: '1px solid #e0e7ff',
                              borderRadius: 2,
                              overflow: 'hidden'
                            }}
                          >
                            <Box
                              onClick={() => setLanguageGroupExpanded(!languageGroupExpanded)}
                              sx={{
                                p: 2,
                                cursor: 'pointer',
                                backgroundColor: languageGroupExpanded ? '#e3f2fd' : '#f8f9fb',
                                display: 'flex',
                                justifyContent: 'space-between',
                                alignItems: 'center',
                                transition: 'all 0.3s ease-in-out',
                                '&:hover': {
                                  backgroundColor: '#e3f2fd',
                                }
                              }}
                            >
                              <Box sx={{ flex: 1 }}>
                                <Typography variant="h6" sx={{ fontWeight: 600, color: '#0168EA', mb: 0.5 }}>
                                  {grouped.languageGroup.groupName}
                                </Typography>
                                <Typography variant="body2" color="textSecondary" sx={{ fontSize: '0.875rem' }}>
                                  {t('you_can_select_only_2_languages') || 'You can select only 2 languages.'}
                                </Typography>
                                {selectedLanguages.length > 0 && (
                                  <Typography variant="caption" sx={{ color: '#4caf50', fontWeight: 500, display: 'block', mt: 0.5 }}>
                                    {t('selected')}: {selectedLanguages.length}/2
                                  </Typography>
                                )}
                              </Box>
                              <IconButton size="small">
                                {languageGroupExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                              </IconButton>
                            </Box>

                            <Collapse in={languageGroupExpanded}>
                              <Box sx={{ p: 2, backgroundColor: '#ffffff' }}>
                                {(() => {
                                  const selectedInLanguageGroupSubgroupA = formData.subjects
                                    .filter(storedValue => storedValue.startsWith('LANGUAGE GROUP|'))
                                    .map(storedValue => {
                                      const code = extractSubjectCode(storedValue);
                                      return String(code).trim();
                                    });

                                  const selectedLanguageCodes = selectedLanguages.map(storedValue => {
                                    const code = extractSubjectCode(storedValue);
                                    return String(code).trim();
                                  });

                                  const filteredLanguageSubjects = (grouped.languageGroup.subjects || []).filter((subject: any) => {
                                    const subjectCode = String(subject.subjectCode || subject.originalCode || '').trim();
                                    const isSelectedInLanguageStep = selectedLanguageCodes.includes(subjectCode);
                                    const isSelectedInLanguageGroup = selectedInLanguageGroupSubgroupA.includes(subjectCode);
                                    return isSelectedInLanguageStep || !isSelectedInLanguageGroup;
                                  });

                                  return renderSubjects(
                                    filteredLanguageSubjects,
                                    2,
                                    selectedLanguages.length,
                                    handleToggleLanguageSubject,
                                    true
                                  );
                                })()}
                              </Box>
                            </Collapse>
                          </Paper>
                        )}

                        {/* Stream Selection */}
                        <Paper
                          elevation={1}
                          sx={{
                            mb: 2,
                            mt: 3,
                            border: '1px solid #e0e7ff',
                            borderRadius: 2,
                            overflow: 'hidden'
                          }}
                        >
                          <Box sx={{ p: 2, backgroundColor: '#f8f9fb' }}>
                            <Typography variant="h6" sx={{ fontWeight: 600, color: '#0168EA', mb: 1 }}>
                              {t('select_stream') || 'Select Stream'}
                            </Typography>
                            {(() => {
                              const selectedLanguages = getSelectedLanguageSubjects();
                              const isStreamDisabled = selectedLanguages.length !== 2;

                              const grouped = getGroupedSubjectOptions();
                              const availableStreamOptions = streamOptions.filter(option => {
                                return grouped.mainGroups && Object.keys(grouped.mainGroups).includes(option.groupName);
                              });

                              return (
                                <TextField
                                  fullWidth
                                  select
                                  label={t('stream') || 'Stream'}
                                  name="stream"
                                  value={selectedStream || (() => {
                                    const streamOption = availableStreamOptions.find(opt => opt.groupName === formData.stream);
                                    return streamOption ? streamOption.value : '';
                                  })() || ''}
                                  onChange={(e) => {
                                    const streamValue = e.target.value;
                                    setSelectedStream(streamValue);

                                    const preservedSubjects = formData.subjects.filter(storedValue => {
                                      const code = extractSubjectCode(storedValue);
                                      const subject = classSubjects?.find((s: any) => s.subjectCode === code);
                                      const name = subject ? subject.name : '';
                                      return isLanguageSubject(code, name) ||
                                        storedValue.startsWith('LANGUAGE|') ||
                                        storedValue.startsWith('LANGUAGE GROUP|');
                                    });

                                    const streamOption = availableStreamOptions.find(opt => opt.value === streamValue);
                                    if (streamOption) {
                                      setFormData(prev => ({
                                        ...prev,
                                        stream: streamOption.groupName,
                                        subjects: preservedSubjects
                                      }));
                                      handleToggleMainGroup(streamOption.groupName);
                                    } else {
                                      setFormData(prev => ({
                                        ...prev,
                                        stream: '',
                                        subjects: preservedSubjects
                                      }));
                                    }
                                  }}
                                  size="small"
                                  margin="dense"
                                  sx={{ backgroundColor: '#ffffff' }}
                                  disabled={isStreamDisabled}
                                  helperText={selectedLanguages.length !== 2 ? (t('please_select_two_languages_first') || 'Please select 2 languages first') : ''}
                                >
                                  <MenuItem value="">
                                    <em>{t('select_stream') || 'Select a stream'}</em>
                                  </MenuItem>
                                  {availableStreamOptions.map((option) => (
                                    <MenuItem key={option.value} value={option.value}>
                                      {option.label} (Group Code: {option.value})
                                    </MenuItem>
                                  ))}
                                </TextField>
                              );
                            })()}
                          </Box>
                        </Paper>

                        {/* Step 2: Main Groups */}
                        {selectedStream && (
                          <>
                            <Typography variant="subtitle2" sx={{ mb: 2, color: '#0168EA', fontWeight: 600, mt: 3 }}>
                              {t('selected_stream_group') || 'Selected Stream Group'}
                            </Typography>
                            {(() => {
                              const streamOption = streamOptions.find(opt => opt.value === selectedStream);
                              if (!streamOption) return null;

                              const groupName = streamOption.groupName;
                              const groupData = (grouped.mainGroups as { [key: string]: any })?.[groupName];

                              if (!groupData) return null;

                              const isGroupSelected = selectedMainGroup === groupName;
                              const isGroupExpanded = expandedGroups.has(groupName);

                              return (
                                <Paper
                                  key={groupName}
                                  elevation={1}
                                  sx={{
                                    mb: 2,
                                    border: '1px solid',
                                    borderColor: isGroupSelected ? '#4caf50' : '#e0e7ff',
                                    borderRadius: 2,
                                    overflow: 'hidden',
                                  }}
                                >
                                  <Box
                                    sx={{
                                      p: 2,
                                      display: 'flex',
                                      alignItems: 'center',
                                      backgroundColor: isGroupSelected ? '#e8f5e9' : '#f8f9fb',
                                      transition: 'all 0.3s ease-in-out',
                                    }}
                                  >
                                    <Box sx={{ flex: 1, display: 'flex', alignItems: 'center' }}>
                                      <Box sx={{ flex: 1 }}>
                                        <Typography variant="h6" sx={{ fontWeight: 600, color: '#0168EA', mb: 0.5 }}>
                                          {groupName}
                                        </Typography>
                                        {groupData.groupCode && (
                                          <Typography variant="caption" color="textSecondary" sx={{ display: 'block' }}>
                                            {t('group_code')}: {groupData.groupCode}
                                          </Typography>
                                        )}
                                        {(() => {
                                          const subjectsInThisGroup = formData.subjects.filter(storedValue =>
                                            storedValue.startsWith(`${groupName}|`)
                                          );
                                          return subjectsInThisGroup.length > 0 ? (
                                            <Typography variant="caption" sx={{ color: '#4caf50', fontWeight: 500, display: 'block', mt: 0.5 }}>
                                              {t('selected_subjects')}: {subjectsInThisGroup.length}/3
                                            </Typography>
                                          ) : null;
                                        })()}
                                      </Box>
                                    </Box>
                                    <IconButton
                                      size="small"
                                      onClick={() => {
                                        setExpandedGroups(prev => {
                                          const newSet = new Set(prev);
                                          if (newSet.has(groupName)) {
                                            newSet.delete(groupName);
                                          } else {
                                            newSet.add(groupName);
                                          }
                                          return newSet;
                                        });
                                      }}
                                    >
                                      {isGroupExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                                    </IconButton>
                                  </Box>

                                  <Collapse in={isGroupExpanded}>
                                    <Box sx={{ p: 2, backgroundColor: '#ffffff' }}>
                                      <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
                                        {t('select_max_3_subjects_from_group') || 'You can select maximum 3 subjects from this group.'}
                                      </Typography>
                                      {groupData.hasSubgroups && groupData.subgroups ? (
                                        groupData.subgroups.map((subgroup: any) => {
                                          const subgroupKey = `${groupName}-${subgroup.subgroupName}`;
                                          const isSubgroupExpanded = expandedSubgroups.has(subgroupKey);

                                          return (
                                            <Box key={subgroup.subgroupName} sx={{ mb: 2 }}>
                                              <Box
                                                onClick={() => handleToggleSubgroup(groupName, subgroup.subgroupName)}
                                                sx={{
                                                  p: 1.5,
                                                  cursor: 'pointer',
                                                  backgroundColor: isSubgroupExpanded ? '#f0f7ff' : '#fafafa',
                                                  borderRadius: 1,
                                                  display: 'flex',
                                                  justifyContent: 'space-between',
                                                  alignItems: 'center',
                                                  mb: 1,
                                                  '&:hover': {
                                                    backgroundColor: '#f0f7ff',
                                                  }
                                                }}
                                              >
                                                <Box sx={{ flex: 1 }}>
                                                  <Typography variant="subtitle1" sx={{ fontWeight: 600, color: '#1976d2' }}>
                                                    {t('subgroup')} {subgroup.subgroupName}
                                                  </Typography>
                                                  {subgroup.description && (
                                                    <Typography variant="body2" color="textSecondary" sx={{ mt: 0.5, fontSize: '0.875rem' }}>
                                                      {subgroup.description}
                                                    </Typography>
                                                  )}
                                                </Box>
                                                <IconButton size="small">
                                                  {isSubgroupExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                                                </IconButton>
                                              </Box>

                                              <Collapse in={isSubgroupExpanded}>
                                                <Box sx={{ pl: 2, pr: 2, pb: 1 }}>
                                                  {renderSubjects(
                                                    subgroup.subjects,
                                                    3,
                                                    formData.subjects.filter(s => s.startsWith(`${groupName}|`)).length,
                                                    (val) => handleToggleMainGroupSubject(val),
                                                    true,
                                                    groupName
                                                  )}
                                                </Box>
                                              </Collapse>
                                            </Box>
                                          );
                                        })
                                      ) : (
                                        <Box sx={{ mt: 1 }}>
                                          {renderSubjects(
                                            groupData.subjects,
                                            3,
                                            formData.subjects.filter(s => s.startsWith(`${groupName}|`)).length,
                                            (val) => handleToggleMainGroupSubject(val),
                                            true,
                                            groupName
                                          )}
                                        </Box>
                                      )}
                                    </Box>
                                  </Collapse>
                                </Paper>
                              );
                            })()}
                          </>
                        )}

                        {/* Step 3: Optional Groups - Additional and NSQF Subjects */}
                        {showOptionalGroups && (
                          <Box sx={{ mt: 3 }}>
                            <Typography variant="subtitle2" sx={{ mb: 2, color: '#0168EA', fontWeight: 600 }}>
                              {t('optional_subjects_step_3') || 'Step 3: Optional Subjects (Select Max 1)'}
                            </Typography>

                            {/* Additional Subject Group */}
                            {grouped.additionalGroup && (
                              <Paper
                                elevation={1}
                                sx={{
                                  mb: 2,
                                  border: '1px solid #e0e7ff',
                                  borderRadius: 2,
                                  overflow: 'hidden'
                                }}
                              >
                                <Box
                                  onClick={() => handleToggleGroup('ADDITIONAL SUBJECT (OPTIONAL)')}
                                  sx={{
                                    p: 2,
                                    cursor: 'pointer',
                                    backgroundColor: expandedGroups.has('ADDITIONAL SUBJECT (OPTIONAL)') ? '#fcf3e8' : '#f8f9fb',
                                    display: 'flex',
                                    justifyContent: 'space-between',
                                    alignItems: 'center',
                                    '&:hover': {
                                      backgroundColor: '#fcf3e8',
                                    }
                                  }}
                                >
                                  <Box sx={{ flex: 1 }}>
                                    <Typography variant="h6" sx={{ fontWeight: 600, color: '#0168EA', mb: 0.5 }}>
                                      {grouped.additionalGroup.groupName}
                                    </Typography>
                                    <Typography variant="body2" color="textSecondary" sx={{ fontSize: '0.875rem' }}>
                                      {t('you_can_select_max_1_optional_subject') || 'You can select maximum 1 optional subject (Additional or NSQF).'}
                                    </Typography>
                                    {(() => {
                                      const isAdditionalSelected = formData.subjects.some(s => s.startsWith('ADDITIONAL SUBJECT (OPTIONAL)|'));
                                      return isAdditionalSelected ? (
                                        <Typography variant="caption" sx={{ color: '#4caf50', fontWeight: 500, display: 'block', mt: 0.5 }}>
                                          {t('selected')}: 1/1
                                        </Typography>
                                      ) : null;
                                    })()}
                                  </Box>
                                  <IconButton size="small">
                                    {expandedGroups.has('ADDITIONAL SUBJECT (OPTIONAL)') ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                                  </IconButton>
                                </Box>

                                <Collapse in={expandedGroups.has('ADDITIONAL SUBJECT (OPTIONAL)')}>
                                  <Box sx={{ p: 2, backgroundColor: '#ffffff' }}>
                                    {renderSubjects(
                                      grouped.additionalGroup.subjects,
                                      1,
                                      selectedOptional ? 1 : 0,
                                      (val) => handleToggleSubject(val),
                                      true,
                                      'ADDITIONAL SUBJECT (OPTIONAL)'
                                    )}
                                  </Box>
                                </Collapse>
                              </Paper>
                            )}

                            {/* NSQF Subject Group */}
                            {grouped.nsqfGroup && (
                              <Paper
                                elevation={1}
                                sx={{
                                  mb: 2,
                                  border: '1px solid #e0e7ff',
                                  borderRadius: 2,
                                  overflow: 'hidden'
                                }}
                              >
                                <Box
                                  onClick={() => handleToggleGroup('NSQF SUBJECT (OPTIONAL)')}
                                  sx={{
                                    p: 2,
                                    cursor: 'pointer',
                                    backgroundColor: expandedGroups.has('NSQF SUBJECT (OPTIONAL)') ? '#f3e5f5' : '#f8f9fb',
                                    display: 'flex',
                                    justifyContent: 'space-between',
                                    alignItems: 'center',
                                    '&:hover': {
                                      backgroundColor: '#f3e5f5',
                                    }
                                  }}
                                >
                                  <Box sx={{ flex: 1 }}>
                                    <Typography variant="h6" sx={{ fontWeight: 600, color: '#0168EA', mb: 0.5 }}>
                                      {grouped.nsqfGroup.groupName}
                                    </Typography>
                                    <Typography variant="body2" color="textSecondary" sx={{ fontSize: '0.875rem' }}>
                                      {t('you_can_select_max_1_optional_subject') || 'You can select maximum 1 optional subject (Additional or NSQF).'}
                                    </Typography>
                                    {(() => {
                                      const isNSQFSelected = formData.subjects.some(s => s.startsWith('NSQF SUBJECT (OPTIONAL)|'));
                                      return isNSQFSelected ? (
                                        <Typography variant="caption" sx={{ color: '#4caf50', fontWeight: 500, display: 'block', mt: 0.5 }}>
                                          {t('selected')}: 1/1
                                        </Typography>
                                      ) : null;
                                    })()}
                                  </Box>
                                  <IconButton size="small">
                                    {expandedGroups.has('NSQF SUBJECT (OPTIONAL)') ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                                  </IconButton>
                                </Box>

                                <Collapse in={expandedGroups.has('NSQF SUBJECT (OPTIONAL)')}>
                                  <Box sx={{ p: 2, backgroundColor: '#ffffff' }}>
                                    {renderSubjects(
                                      grouped.nsqfGroup.subjects,
                                      1,
                                      selectedOptional ? 1 : 0,
                                      (val) => handleToggleSubject(val),
                                      true,
                                      'NSQF SUBJECT (OPTIONAL)'
                                    )}
                                  </Box>
                                </Collapse>
                              </Paper>
                            )}
                          </Box>
                        )}

                        {/* Validation Summary and Rule Alerts */}
                        <Box sx={{ mt: 3 }}>
                          {selectedLanguages.length < 2 && (
                            <Alert severity="warning" sx={{ mb: 1 }}>
                              {t('two_languages_required') || 'Please select 2 languages to proceed with stream selection.'}
                            </Alert>
                          )}
                          {selectedLanguages.length === 2 && !selectedStream && (
                            <Alert severity="info" sx={{ mb: 1 }}>
                              {t('select_stream_to_view_main_groups') || 'Please select a stream to view available main group subjects.'}
                            </Alert>
                          )}
                          {selectedStream && selectedMainGroupSubjects.length < 3 && (
                            <Alert severity="warning" sx={{ mb: 1 }}>
                              {t('select_exactly_3_main_group_subjects') || 'Please select exactly 3 subjects from your chosen stream.'}
                            </Alert>
                          )}
                          {validationErrors.subjects && (
                            <Alert severity="error" sx={{ mb: 1 }}>
                              {validationErrors.subjects}
                            </Alert>
                          )}
                        </Box>
                      </Box>
                    );
                  })()}
                </Grid>
              ) : (
                /* For Other Classes: Show Dropdown */
                <Grid item xs={12} md={4}>
                  <TextField
                    fullWidth
                    select
                    label={t('add_subject')}
                    name="addSubject"
                    value=""
                    onChange={handleChange}
                    size="small"
                    margin="dense"
                    sx={{ backgroundColor: '#f8f9fb' }}
                    helperText={
                      subjectsLoading
                        ? t("loading_subjects")
                        : !selectedAdmissionClass
                          ? t("please_select_class_first")
                          : !formData.sessionName
                            ? t("please_select_session_first") || "Please select session first"
                            : !classSubjects || classSubjects.length === 0
                              ? t("no_subjects_available_for_class")
                              : formData.subjects.length >= 6
                                ? t("max_subjects_reached")
                                : t('select_subject_to_add')
                    }
                    disabled={subjectsLoading || !selectedAdmissionClass || !formData.sessionName || !classSubjects || classSubjects.length === 0 || formData.subjects.length >= 6 || isFieldReadOnly('subjects')}
                    SelectProps={{
                      MenuProps: {
                        anchorOrigin: {
                          vertical: 'bottom',
                          horizontal: 'left',
                        },
                        transformOrigin: {
                          vertical: 'top',
                          horizontal: 'left',
                        },
                        PaperProps: {
                          style: {
                            maxHeight: 300,
                            zIndex: 9999,
                          },
                        },
                      },
                    }}
                  >
                    {subjectsLoading ? (
                      <MenuItem disabled>{t("loading_subjects")}</MenuItem>
                    ) : !classSubjects || classSubjects.length === 0 ? (
                      <MenuItem disabled>{t("no_subjects_available")}</MenuItem>
                    ) : (
                      getFilteredSubjectOptions().map((option: any) => (
                        <MenuItem key={option.value} value={option.value}>
                          {option.label}
                        </MenuItem>
                      ))
                    )}
                  </TextField>
                </Grid>
              )}

              {/* Student Photo and Signature Upload Section */}
              <Grid item xs={12} md={8}>
                <Grid container spacing={1}>
                  <Grid item xs={12} sm={6}>
                    <Paper
                      variant="outlined"
                      sx={{
                        height: '40px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        px: 1.5,
                        bgcolor: '#f8f9fb',
                        mb: 1
                      }}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        {imagePreview || authenticatedImageUrl ? (
                          <Avatar
                            src={imagePreview || authenticatedImageUrl || undefined}
                            sx={{
                              width: 24,
                              height: 24,
                              border: '1px solid #0168EA',
                              cursor: 'pointer'
                            }}
                            onClick={() => setImagePreviewModal(true)}
                          />
                        ) : (
                          <PhotoCameraIcon fontSize="small" color="primary" />
                        )}
                        <Typography variant="body2">{t('student_photo')}</Typography>
                      </Box>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        {(imagePreview || authenticatedImageUrl) && (
                          <Button
                            variant="outlined"
                            size="small"
                            onClick={handleRemoveImage}
                            sx={{
                              minWidth: 'auto',
                              p: '4px 8px',
                              borderColor: '#f44336',
                              color: '#f44336',
                              '&:hover': {
                                backgroundColor: '#ffebee',
                                borderColor: '#d32f2f'
                              }
                            }}
                          >
                            <DeleteIcon fontSize="small" />
                          </Button>
                        )}
                        <Button
                          variant="outlined"
                          component="label"
                          size="small"
                          sx={{ minWidth: 'auto', p: '4px 8px' }}
                        >
                          {imagePreview || authenticatedImageUrl ? (
                            <CheckCircleIcon fontSize="small" color="success" />
                          ) : (
                            <CloudUploadIcon fontSize="small" />
                          )}
                          <input
                            type="file"
                            hidden
                            accept="image/*"
                            onChange={handleImageChange}
                          />
                        </Button>
                      </Box>
                    </Paper>
                    {imageError && (
                      <Typography variant="caption" color="error" sx={{ mt: 0.5, display: 'block' }}>
                        {imageError}
                      </Typography>
                    )}
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <Paper
                      variant="outlined"
                      sx={{
                        height: '40px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        px: 1.5,
                        bgcolor: '#f8f9fb'
                      }}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        {signaturePreview || authenticatedSignatureUrl ? (
                          <Avatar
                            src={signaturePreview || authenticatedSignatureUrl || undefined}
                            sx={{
                              width: 24,
                              height: 24,
                              border: '1px solid #0168EA',
                              cursor: 'pointer'
                            }}
                            onClick={() => setSignaturePreviewModal(true)}
                          />
                        ) : (
                          <PhotoCameraIcon fontSize="small" color="primary" />
                        )}
                        <Typography variant="body2">{t('student_signature')}</Typography>
                      </Box>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        {(signaturePreview || authenticatedSignatureUrl) && (
                          <Button
                            variant="outlined"
                            size="small"
                            onClick={handleRemoveSignature}
                            sx={{
                              minWidth: 'auto',
                              p: '4px 8px',
                              borderColor: '#f44336',
                              color: '#f44336',
                              '&:hover': {
                                backgroundColor: '#ffebee',
                                borderColor: '#d32f2f'
                              }
                            }}
                          >
                            <DeleteIcon fontSize="small" />
                          </Button>
                        )}
                        <Button
                          variant="outlined"
                          component="label"
                          size="small"
                          sx={{ minWidth: 'auto', p: '4px 8px' }}
                        >
                          {signaturePreview || authenticatedSignatureUrl ? (
                            <CheckCircleIcon fontSize="small" color="success" />
                          ) : (
                            <CloudUploadIcon fontSize="small" />
                          )}
                          <input
                            type="file"
                            hidden
                            accept="image/*"
                            onChange={handleSignatureChange}
                          />
                        </Button>
                      </Box>
                    </Paper>
                    {signatureError && (
                      <Typography variant="caption" color="error" sx={{ mt: 0.5, display: 'block' }}>
                        {signatureError}
                      </Typography>
                    )}
                  </Grid>
                </Grid>
              </Grid>

              {/* Selected Subjects Display */}
              <Grid item xs={12}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                  <Typography variant="subtitle2" sx={{ color: '#0168EA', fontWeight: 600 }}>
                    {t('selected_subjects')} ({formData.subjects.length}/6)
                  </Typography>
                  {formData.subjects.length >= 6 && (
                    <Typography variant="caption" sx={{ color: '#d32f2f', fontWeight: 500 }}>
                      {t('max_subjects_reached')}
                    </Typography>
                  )}
                </Box>

                {formData.subjects.length === 0 ? (
                  <Box sx={{
                    p: 2,
                    textAlign: 'center',
                    backgroundColor: '#f8f9fb',
                    borderRadius: 1,
                    border: '1px dashed #ccc'
                  }}>
                    <Typography variant="body2" color="textSecondary">
                      {t('no_subjects_selected')}
                    </Typography>
                  </Box>
                ) : (
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                    {getSortedSubjects().map((subjectValue, index) => (
                      <Box
                        key={index}
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 1,
                          p: 1.5,
                          backgroundColor: '#e3f2fd',
                          borderRadius: 2,
                          border: '1px solid #90caf9',
                          transition: 'all 0.2s ease-in-out',
                          '&:hover': {
                            backgroundColor: '#bbdefb'
                          }
                        }}
                      >
                        <Typography variant="body2" sx={{ fontWeight: 500 }}>
                          {getSubjectDisplayName(subjectValue)}
                        </Typography>
                        <Button
                          size="small"
                          variant="outlined"
                          color="error"
                          onClick={() => handleRemoveSubject(index)}
                          disabled={isFieldReadOnly('subjects')}
                          sx={{
                            minWidth: 'auto',
                            p: 1,
                            borderColor: '#f44336',
                            color: '#f44336',
                            '&:hover': {
                              backgroundColor: '#ffebee',
                              borderColor: '#d32f2f'
                            }
                          }}
                        >
                          ×
                        </Button>
                      </Box>
                    ))}
                  </Box>
                )}
              </Grid>
            </Grid>
          </Paper>
        </DialogContent>
        <DialogActions sx={{ p: 1.5, gap: 2, backgroundColor: '#f8f9fb' }}>
          <Button
            onClick={() => {
              // Save draft data when canceling (only for create mode)
              if (!isEdit) {
                const hasData = formData.studentName ||
                  formData.fatherName ||
                  formData.motherName ||
                  formData.mobileNo ||
                  formData.dateOfBirth ||
                  formData.gender ||
                  formData.admissionClass ||
                  formData.sessionName;

                if (hasData) {
                  // Save draft data excluding File objects (they can't be serialized)
                  const draftDataToSave = {
                    ...formData,
                    studentPhoto: null, // Don't save File objects
                    studentSignature: null, // Don't save File objects
                    // Keep photo/signature URLs if they exist
                    studentPhotoUrl: formData.studentPhotoUrl,
                    studentSignatureUrl: formData.studentSignatureUrl,
                  };
                  saveApplicationFormDraft(draftDataToSave);
                } else {
                  // Clear draft if form is empty
                  clearApplicationFormDraft();
                }
              }
              onClose();
            }}
            variant="outlined"
            size="large"
            sx={{
              minWidth: 120,
              borderColor: '#3092e3',
              color: '#3092e3',
              '&:hover': {
                borderColor: '#0056d3',
                backgroundColor: 'rgba(1, 104, 234, 0.04)'
              }
            }}
          >
            {t('cancel')}
          </Button>
          <Button
            onClick={async () => {
              // Validate required fields
              const errors: Record<string, string> = {};

              if (!formData.studentName?.trim()) {
                errors.studentName = t('required_field') || 'Required field';
              }
              if (!formData.fatherName?.trim()) {
                errors.fatherName = t('required_field') || 'Required field';
              }
              if (!formData.motherName?.trim()) {
                errors.motherName = t('required_field') || 'Required field';
              }
              if (!formData.gender?.trim()) {
                errors.gender = t('required_field') || 'Required field';
              }
              if (!formData.dateOfBirth?.trim()) {
                errors.dateOfBirth = t('required_field') || 'Required field';
              }
              if (!formData.mobileNo?.trim()) {
                errors.mobileNo = t('required_field') || 'Required field';
              }
              // Validate session first (required before class selection)
              if (!formData.sessionName?.trim() || !formData.sessionId || formData.sessionId === 0) {
                errors.sessionName = t('required_field') || 'Required field';
              }
              // Check both admissionClass and selectedClassId since the field uses selectedClassId
              // Only validate class if session is selected
              if (formData.sessionName?.trim() && formData.sessionId && formData.sessionId !== 0) {
                if (!formData.admissionClass?.trim() && !selectedClassId) {
                  errors.admissionClass = t('required_field') || 'Required field';
                }
              } else {
                // If session is not selected, show message to select session first
                if (!formData.admissionClass?.trim() && !selectedClassId) {
                  errors.admissionClass = t('please_select_session_first') || 'Please select a session first';
                }
              }
              if (!formData.medium?.trim()) {
                errors.medium = t('required_field') || 'Required field';
              }

              // Set validation errors
              setValidationErrors(errors);

              // If there are validation errors, don't submit
              if (Object.keys(errors).length > 0) {
                return;
              }

              // Find the selected class by ID first, then fallback to name
              const selectedClassData = selectedClassId
                ? availableClasses?.find(cls => cls.id === selectedClassId)
                : availableClasses?.find(cls => cls.name === formData.admissionClass);

              // Ensure all required fields are included with proper defaults
              const finalFormData: AdmissionFromData = {
                // Application & Enrollment Details
                applicationNo: formData.applicationNo || '',
                rollNo: formData.rollNo || '',
                enrolmentNo: formData.enrolmentNo || '',
                scholarNo: formData.scholarNo || '',
                sssmid: formData.sssmid || '',
                penNo: formData.penNo || '',
                apparId: formData.apparId || '',
                studentCode: formData.studentCode || '',

                // Personal Information
                studentName: formData.studentName || '',
                fatherName: formData.fatherName || '',
                motherName: formData.motherName || '',
                dateOfBirth: formData.dateOfBirth || '',
                gender: formData.gender || '',
                religion: formData.religion || '',
                caste: formData.caste || '',
                subCaste: formData.subCaste || '',
                casteCertificate: formData.casteCertificate || '',
                aadharNo: formData.aadharNo || '',
                studentPhoto: formData.studentPhoto ?? null,
                studentSignature: formData.studentSignature ?? null,

                // Contact & Address
                houseNo: formData.houseNo || '',
                street: formData.street || '',
                landmark: formData.landmark || '',
                area: formData.area || '',
                city: formData.city || '',
                postOffice: formData.postOffice || '',
                district: formData.district || '',
                state: formData.state || '',
                pinCode: formData.pinCode || '',
                mobileNo: formData.mobileNo || user?.email || '',
                gmail: formData.gmail || '',

                // Bank & Financial
                bankName: formData.bankName || '',
                ifcCode: formData.ifcCode || '',
                accountNo: formData.accountNo || '',
                annualIncome: formData.annualIncome || '',

                // Admission Details
                admissionDate: formData.admissionDate || '',
                admissionClass: formData.admissionClass || '',
                section: formData.section || '',
                medium: formData.medium || '',
                admissionType: formData.admissionType || '',
                stream: formData.stream || '', // Stream field with default empty string
                cwsnCode: formData.cwsnCode || '',
                sambalKramank: formData.sambalKramank || '',

                // Subjects
                subjects: getSortedSubjects().map(storedValue => extractSubjectCode(storedValue)),

                // System fields
                schoolId: formData.schoolId || 0,
                classId: selectedClassData?.id || formData.classId || undefined,
                sessionId: formData.sessionId || 0,
                sessionName: formData.sessionName || '',
                isRegistered: formData.isRegistered || 'Pending',

                // Include ID if editing
                ...(isEdit && formData.id ? { id: formData.id } : {}),
              };

              // Don't clear draft here - it should be cleared by parent after successful submission
              // This prevents data loss if submission fails

              // Submit the form data
              onSubmit(finalFormData);
            }}
            variant="contained"
            size="large"
            sx={{
              minWidth: 120,
              backgroundColor: '#3092e3',
              boxShadow: '0 4px 12px rgba(66, 165, 245, 0.3)',
              '&:hover': {
                background: 'linear-gradient(135deg, #0056d3 0%, #2f7de8 100%)',
                boxShadow: '0 6px 16px rgba(1, 104, 234, 0.4)'
              }
            }}
          >
            {isEdit ? t('update') : t('submit')}
          </Button>
        </DialogActions>

        {/* Image Preview Modal */}
        <Dialog
          open={imagePreviewModal}
          onClose={() => setImagePreviewModal(false)}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle>
            <Typography variant="h6">{t('student_photo_preview')}</Typography>
          </DialogTitle>
          <DialogContent sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', p: 3 }}>
            {(imagePreview || authenticatedImageUrl) && (
              <Box
                component="img"
                src={imagePreview || authenticatedImageUrl || undefined}
                alt="Student Photo Preview"
                sx={{
                  maxWidth: '100%',
                  maxHeight: '70vh',
                  objectFit: 'contain',
                  borderRadius: 2,
                  boxShadow: '0 4px 20px rgba(0,0,0,0.1)'
                }}
              />
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setImagePreviewModal(false)} variant="contained">
              {t('close')}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Signature Preview Modal */}
        <Dialog
          open={signaturePreviewModal}
          onClose={() => setSignaturePreviewModal(false)}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle>
            <Typography variant="h6">{t('student_signature')} {t('preview') || 'Preview'}</Typography>
          </DialogTitle>
          <DialogContent sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', p: 3 }}>
            {(signaturePreview || authenticatedSignatureUrl) && (
              <Box
                component="img"
                src={signaturePreview || authenticatedSignatureUrl || undefined}
                alt="Student Signature Preview"
                sx={{
                  maxWidth: '100%',
                  maxHeight: '70vh',
                  objectFit: 'contain',
                  borderRadius: 2,
                  boxShadow: '0 4px 20px rgba(0,0,0,0.1)'
                }}
              />
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setSignaturePreviewModal(false)} variant="contained">
              {t('close')}
            </Button>
          </DialogActions>
        </Dialog>
      </Dialog>
    </LocalizationProvider>
  );
};

